import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { apiClient, API_URL, getEnvironmentInfo, isValidEnvironment, formatP0Error, formatScopeAuthorityError, projectGet, projectPost, createProjectHeaders, createProjectHeadersSafe, normalizeProjectId, SCOPE_AUTHORITY_ERROR_CODES, buildStructuredToast, isErrorEnvelope, isMetaOAuthError, buildMetaOAuthToast, META_OAUTH_ERROR_CODES, classifyApiError, isAmeAuthError, AME_AUTH_ERROR_CODES, authGet, authPost } from '../lib/api';
import { handleActionError, IntegrationStateManager } from '../lib/errorHandlers';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { ArrowLeft, Zap, CheckCircle, XCircle, AlertCircle, AlertTriangle, RefreshCw, MapPin, Building, Sparkles, Tag, Calendar, Users, Loader2, Twitter, Eye, EyeOff, ExternalLink, Copy, ChevronDown, ChevronUp, Send, Trash2, HelpCircle, Settings, MessageCircle, Bot, Hash, Link as LinkIcon, Search, X, Save, Shield } from 'lucide-react';
import DomainSetup from '../components/DomainSetup';

// Log API URL for debugging
console.info('[ProjectSettings] API_URL:', API_URL, '| Current Host:', window.location.host);

const CHANNELS = [
  { id: 'telegram', name: 'Telegram', description: 'Messages automatiques' },
  { id: 'x', name: 'X (Twitter)', description: 'Posts automatiques' },
  { id: 'facebook', name: 'Facebook', description: 'Posts sur Pages' },
  { id: 'instagram', name: 'Instagram', description: 'Posts et stories' },
  { id: 'linkedin', name: 'LinkedIn', description: 'Posts professionnels' },
  { id: 'tiktok', name: 'TikTok', description: 'Vidéos courtes' },
  { id: 'youtube', name: 'YouTube', description: 'Vidéos (semi-auto)' },
  { id: 'blog', name: 'Blog / CMS', description: 'Publication automatique d\'articles' },
  { id: 'email', name: 'Email', description: 'Campagnes newsletter' },
];

export default function ProjectSettings() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const { t } = useI18n();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recrawling, setRecrawling] = useState(false);
  const [editingThemes, setEditingThemes] = useState(null);
  
  // X Integration State
  const [xStatus, setXStatus] = useState(null);
  const [xLoading, setXLoading] = useState(true);
  const [xSaving, setXSaving] = useState(false);
  const [xConnecting, setXConnecting] = useState(false);
  const [xTesting, setXTesting] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [showXInstructions, setShowXInstructions] = useState(false);
  const [testTweetText, setTestTweetText] = useState('Test depuis AI Marketing Engine ! 🚀');

  // Telegram Integration State
  const [tgStatus, setTgStatus] = useState(null);
  const [tgLoading, setTgLoading] = useState(true);
  const [tgSaving, setTgSaving] = useState(false);
  const [tgTesting, setTgTesting] = useState(false);
  const [tgFetchingChats, setTgFetchingChats] = useState(false);
  const [tgBotToken, setTgBotToken] = useState('');
  const [tgChatId, setTgChatId] = useState('');
  const [tgParseMode, setTgParseMode] = useState('HTML');
  const [tgDisablePreview, setTgDisablePreview] = useState(false);
  const [tgEnabled, setTgEnabled] = useState(true);
  const [showTgToken, setShowTgToken] = useState(false);
  const [tgAvailableChats, setTgAvailableChats] = useState([]);

  // Facebook Integration State
  const [fbStatus, setFbStatus] = useState(null);
  const [fbDiag, setFbDiag] = useState(null);  // Diagnostic détaillé
  const [fbLoading, setFbLoading] = useState(true);
  const [fbSaving, setFbSaving] = useState(false);
  const [fbConnecting, setFbConnecting] = useState(false);
  const [fbTesting, setFbTesting] = useState(false);
  const [fbAppId, setFbAppId] = useState('');
  const [fbAppSecret, setFbAppSecret] = useState('');
  const [fbDefaultHashtags, setFbDefaultHashtags] = useState('');
  const [fbEnabled, setFbEnabled] = useState(true);
  const [fbAutoPost, setFbAutoPost] = useState(false);
  const [showFbSecret, setShowFbSecret] = useState(false);
  const [fbPages, setFbPages] = useState([]);
  const [fbLoadingPages, setFbLoadingPages] = useState(false);
  const [fbPageModalOpen, setFbPageModalOpen] = useState(false);
  const [fbLastTest, setFbLastTest] = useState(null);
  const [fbPollingActive, setFbPollingActive] = useState(false);
  const fbPollingRef = React.useRef(null);
  const fbPollingCountRef = React.useRef(0);
  // Multi-tenant Facebook App config
  const [fbUseCustomApp, setFbUseCustomApp] = useState(false);
  const [fbHasDefaultApp, setFbHasDefaultApp] = useState(false);
  const [fbAppConfigSource, setFbAppConfigSource] = useState('NONE');

  // Instagram Integration State
  const [igStatus, setIgStatus] = useState(null);
  const [igDiag, setIgDiag] = useState(null);
  const [igUnifiedStatus, setIgUnifiedStatus] = useState(null); // NEW: Unified status for POST_READY logic
  const [igLoading, setIgLoading] = useState(true);
  const [igSaving, setIgSaving] = useState(false);
  const [igConnecting, setIgConnecting] = useState(false);
  const [igTesting, setIgTesting] = useState(false);
  const [igDisconnecting, setIgDisconnecting] = useState(false); // NEW: Disconnect loading state
  const [igDisconnectModalOpen, setIgDisconnectModalOpen] = useState(false); // NEW: Disconnect confirm modal
  const [igError, setIgError] = useState(null);  // Structured error state
  const [igAppId, setIgAppId] = useState('');
  const [igAppSecret, setIgAppSecret] = useState('');
  const [igDefaultHashtags, setIgDefaultHashtags] = useState('');
  const [igEnabled, setIgEnabled] = useState(true);
  const [showIgSecret, setShowIgSecret] = useState(false);
  const [igAccounts, setIgAccounts] = useState([]);
  const [igLoadingAccounts, setIgLoadingAccounts] = useState(false);
  const [igAccountModalOpen, setIgAccountModalOpen] = useState(false);
  const [igUseCustomApp, setIgUseCustomApp] = useState(false);
  const [igInfoOpen, setIgInfoOpen] = useState(false);
  const [igTutorialOpen, setIgTutorialOpen] = useState(false);
  const [igTutorial, setIgTutorial] = useState(null);
  const [igRefreshing, setIgRefreshing] = useState(false); // NEW: Refresh accounts loading state
  const [igAppReviewCheck, setIgAppReviewCheck] = useState(null); // NEW: App Review check results
  const [igAppReviewLoading, setIgAppReviewLoading] = useState(false); // NEW: App Review check loading
  const [igAppReviewOpen, setIgAppReviewOpen] = useState(false); // NEW: App Review results modal
  const [showAccessDeniedHelp, setShowAccessDeniedHelp] = useState(false); // NEW: OAuth access denied help modal
  // Discovery retry state (P0 Hardening v2.2)
  const [igDiscoveryRetrying, setIgDiscoveryRetrying] = useState(false);
  const [igDiscoveryRetryAfter, setIgDiscoveryRetryAfter] = useState(null); // Countdown in seconds
  const [igDiscoveryLocked, setIgDiscoveryLocked] = useState(false);
  const [igLastAttemptId, setIgLastAttemptId] = useState(null); // For observability
  const [igLastErrorSource, setIgLastErrorSource] = useState(null); // Last error source
  const [igPagesWithIg, setIgPagesWithIg] = useState([]); // Multi-page handling
  const [igPageSelectionModalOpen, setIgPageSelectionModalOpen] = useState(false); // Page selection modal

  // Meta Debug Bundle State (P0 - Production Debugging)
  const [metaDebugBundle, setMetaDebugBundle] = useState(null);
  const [metaDebugLoading, setMetaDebugLoading] = useState(false);
  const isDebugMode = searchParams.get('debug') === '1';

  // Backend Version State (P0 FINAL - Anti-cache/Versioning)
  const [backendVersion, setBackendVersion] = useState(null);

  // Brevo (Email) Integration State
  const [brevoStatus, setBrevoStatus] = useState(null);
  const [brevoLoading, setBrevoLoading] = useState(true);
  const [brevoSaving, setBrevoSaving] = useState(false);
  const [brevoTesting, setBrevoTesting] = useState(false);
  const [brevoApiKey, setBrevoApiKey] = useState('');
  const [brevoSenderEmail, setBrevoSenderEmail] = useState('');
  const [brevoSenderName, setBrevoSenderName] = useState('');
  const [brevoEnabled, setBrevoEnabled] = useState(true);
  const [brevoAutoSend, setBrevoAutoSend] = useState(false);
  const [showBrevoKey, setShowBrevoKey] = useState(false);
  const [brevoInfoOpen, setBrevoInfoOpen] = useState(false);

  // Info modals state for all integrations
  const [xInfoOpen, setXInfoOpen] = useState(false);
  const [tgInfoOpen, setTgInfoOpen] = useState(false);
  const [fbInfoOpen, setFbInfoOpen] = useState(false);

  // Handle OAuth callback params
  useEffect(() => {
    const xConnected = searchParams.get('x_connected');
    const xError = searchParams.get('x_error');
    const fbConnected = searchParams.get('facebook');
    const fbError = searchParams.get('facebook_error');
    const fbPage = searchParams.get('page');
    
    if (fbConnected === 'connected') {
      if (fbPage) {
        toast.success(`Facebook connecté ! Page sélectionnée: ${fbPage}`);
      } else {
        toast.success('Facebook connecté avec succès !');
      }
      fetchFacebookStatus();
      navigate(location.pathname, { replace: true });
    }
    
    if (fbConnected === 'select_page') {
      toast.info('Facebook connecté ! Sélectionnez maintenant votre Page.', {
        duration: 5000,
        icon: '📄'
      });
      // Fetch status then open page selector automatically
      fetchFacebookStatus().then(() => {
        setTimeout(() => {
          handleOpenPageSelector();
        }, 500);
      });
      navigate(location.pathname, { replace: true });
    }
    
    if (fbConnected === 'no_pages') {
      toast.warning('OAuth réussi mais aucune Page trouvée. Vérifiez que vous êtes admin d\'une Page Facebook.', {
        duration: 8000,
        icon: '⚠️'
      });
      fetchFacebookStatus();
      navigate(location.pathname, { replace: true });
    }
    
    if (fbError) {
      const errorMessages = {
        'token_exchange_failed': 'Échec de l\'échange de token. Vérifiez vos credentials.',
        'state_mismatch': 'Erreur de sécurité (CSRF). Veuillez réessayer.',
        'missing_code': 'Code d\'autorisation manquant.',
        'missing_state': 'Paramètre de sécurité manquant.',
        'no_credentials': 'Configuration Facebook non trouvée.',
        'pages_fetch_failed': 'Impossible de récupérer vos Pages.',
        'no_pages': 'Aucune Page Facebook trouvée. Créez une Page sur Facebook d\'abord.',
        'callback_error': 'Erreur lors du callback OAuth.',
        'invalid_scopes': 'Scopes Facebook invalides. Vérifiez la configuration de votre app Meta.',
        'no_pages_access': 'Impossible d\'accéder à vos Pages. Vérifiez les permissions.',
        'APP_CONFIG_MISSING': 'Configuration de l\'app Meta manquante.',
        'NO_APP_CONFIGURED': 'Aucune app Meta configurée.'
      };
      const message = errorMessages[fbError] || `Erreur Facebook: ${fbError}`;
      toast.error(message);
      fetchFacebookStatus();
      navigate(location.pathname, { replace: true });
    }
    
    // OAUTH ACCESS DENIED - Special handling with help links
    const oauthError = searchParams.get('oauth_error');
    const errorCode = searchParams.get('error_code');
    
    if (oauthError === 'access_denied' || errorCode === 'OAUTH_ACCESS_DENIED') {
      setShowAccessDeniedHelp(true);
      navigate(location.pathname, { replace: true });
    }
    
    // Instagram OAuth callbacks
    const igConnected = searchParams.get('instagram');
    const igError = searchParams.get('instagram_error');
    const igUsername = searchParams.get('ig_username');
    
    if (igConnected === 'connected') {
      if (igUsername) {
        toast.success(`Instagram connecté ! Compte: @${igUsername}`);
      } else {
        toast.success('Instagram connecté avec succès !');
      }
      fetchInstagramStatus();
      navigate(location.pathname, { replace: true });
    }
    
    if (igConnected === 'select_account') {
      toast.info('Instagram connecté ! Sélectionnez maintenant votre compte.', {
        duration: 5000,
        icon: '📷'
      });
      fetchInstagramStatus().then(() => {
        setTimeout(() => {
          setIgAccountModalOpen(true);
        }, 500);
      });
      navigate(location.pathname, { replace: true });
    }
    
    if (igConnected === 'no_ig_account') {
      toast.warning('OAuth réussi mais aucun compte Instagram Business trouvé. Convertissez votre compte en Pro et liez-le à une Page Facebook.', {
        duration: 10000,
        icon: '⚠️'
      });
      fetchInstagramStatus();
      navigate(location.pathname, { replace: true });
    }
    
    if (igError) {
      const errorMessages = {
        'token_exchange_failed': 'Échec de l\'échange de token. Vérifiez vos credentials.',
        'state_mismatch': 'Erreur de sécurité (CSRF). Veuillez réessayer.',
        'no_credentials': 'Configuration Instagram non trouvée.',
        'pages_fetch_failed': 'Impossible de récupérer vos Pages.',
        'no_ig_account': 'Aucun compte Instagram Business trouvé.'
      };
      const message = errorMessages[igError] || `Erreur Instagram: ${igError}`;
      toast.error(message);
      fetchInstagramStatus();
      navigate(location.pathname, { replace: true });
    }
    
    if (xConnected === 'true') {
      toast.success(t('xConnectedSuccess') || 'Compte X connecté avec succès !');
      window.history.replaceState({}, '', `/projects/${projectId}/settings`);
      fetchXStatus();
    }
    
    if (xError) {
      toast.error(`${t('xConnectionError') || 'Erreur de connexion X'}: ${xError}`);
      window.history.replaceState({}, '', `/projects/${projectId}/settings`);
    }
  }, [searchParams, projectId, t]);

  useEffect(() => {
    fetchProject();
    fetchXStatus();
    fetchTelegramStatus();
    fetchFacebookStatus();
    fetchInstagramStatus();
    fetchBrevoStatus();
    fetchCallbackUrls();
    fetchBackendVersion();
  }, [projectId]);

  // Fetch Backend Version (P0 FINAL - Anti-cache/Versioning)
  const fetchBackendVersion = async () => {
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.get('/version');
      setBackendVersion(response.data);
    } catch (error) {
      console.error('[Version] Failed to fetch backend version:', error);
    }
  };

  // Hard refresh function (clears all caches)
  const handleHardRefresh = async () => {
    try {
      // Clear service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }
      
      // Clear caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Clear localStorage items related to cache
      const keysToRemove = [];
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && (key.includes('cache') || key.includes('version'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      toast.success('Cache vidé ! Rechargement...', { duration: 2000 });
      
      // Force reload with cache bypass
      setTimeout(() => {
        window.location.href = window.location.pathname + '?_t=' + Date.now();
      }, 1000);
    } catch (error) {
      console.error('[HardRefresh] Error:', error);
      toast.error('Erreur lors du vidage du cache');
    }
  };

  const fetchProject = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      toast.error(t('errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  // X Integration Functions
  const fetchXStatus = async () => {
    try {
      // P0 FIX: Pass project_id to get accurate project-scoped status
      const response = await authGet(`/integrations/x/status?project_id=${projectId}`);
      setXStatus(response.data);
      // Use production domain for callback URL
      const callbackBase = window.location.host.includes('ai-marketing-engine.com') 
        ? 'https://ai-marketing-engine.com' 
        : window.location.origin;
      setRedirectUri(`${callbackBase}/api/auth/x/callback`);
    } catch (error) {
      // P0 DEFINITIVE: Classify error - don't show misleading messages
      const classified = classifyApiError(error);
      if (classified.type === 'AME_AUTH') {
        console.warn('[fetchXStatus] AME auth required:', classified.message);
        // Don't show toast spam - auth banner will handle it
      } else {
        console.error('Failed to fetch X status:', error);
      }
      // Fallback to production domain
      const callbackBase = window.location.host.includes('ai-marketing-engine.com') 
        ? 'https://ai-marketing-engine.com' 
        : window.location.origin;
      setRedirectUri(`${callbackBase}/api/auth/x/callback`);
    } finally {
      setXLoading(false);
    }
  };

  // State for callback URLs
  const [callbackUrls, setCallbackUrls] = useState({});
  
  const fetchCallbackUrls = async () => {
    try {
      // P0 HARDENING: Use authGet with proper error classification
      const response = await authGet('/integrations/callback-urls');
      setCallbackUrls(response.data);
    } catch (error) {
      // P0 DEFINITIVE: Classify error but don't show spam
      const classified = classifyApiError(error);
      if (classified.type !== 'AME_AUTH') {
        console.error('Failed to fetch callback URLs:', error);
      }
      // Fallback to generating URLs from current domain
      const baseUrl = window.location.origin;
      setCallbackUrls({
        x: `${baseUrl}/api/auth/x/callback`,
        facebook: `${baseUrl}/api/auth/facebook/callback`,
        instagram: `${baseUrl}/api/auth/instagram/callback`,
        linkedin: `${baseUrl}/api/auth/linkedin/callback`,
        tiktok: `${baseUrl}/api/auth/tiktok/callback`
      });
    }
  };

  const handleSaveXCredentials = async () => {
    if (!clientId) {
      toast.error(t('clientIdRequired') || 'Client ID est requis');
      return;
    }
    setXSaving(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/integrations/x/credentials', {
        client_id: clientId,
        client_secret: clientSecret || null,
        redirect_uri: redirectUri || null
      });
      if (response.data.success) {
        toast.success(t('credentialsSaved') || 'Credentials sauvegardées !');
        setClientId('');
        setClientSecret('');
        fetchXStatus();
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la sauvegarde');
    } finally {
      setXSaving(false);
    }
  };

  const handleConnectX = async () => {
    setXConnecting(true);
    try {
      // P0 FIX DEFINITIVE: project_id is REQUIRED for X OAuth start
      if (!projectId) {
        toast.error('Aucun projet sélectionné. Veuillez d\'abord créer ou sélectionner un projet.');
        setXConnecting(false);
        return;
      }
      const response = await apiClient.get(`/auth/x/start?project_id=${projectId}`);
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors du démarrage OAuth');
      setXConnecting(false);
    }
  };

  const handleDisconnectX = async () => {
    if (!window.confirm(t('confirmDisconnectX') || 'Êtes-vous sûr de vouloir déconnecter votre compte X ?')) return;
    try {
      // P0 FIX: Use new disconnect endpoint with project_id
      await apiClient.post(`/integrations/x/disconnect?project_id=${projectId}`);
      toast.success(t('xDisconnected') || 'Compte X déconnecté');
      fetchXStatus();
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la déconnexion');
    }
  };

  const handleDeleteXCredentials = async () => {
    if (!window.confirm(t('confirmDeleteCredentials') || 'Êtes-vous sûr de vouloir supprimer vos credentials X ?')) return;
    try {
      // P0 HARDENING: Use centralized apiClient
      await apiClient.delete('/integrations/x/credentials');
      toast.success(t('credentialsDeleted') || 'Credentials supprimées');
      setXStatus(null);
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la suppression');
    }
  };

  const handleTestXPost = async () => {
    if (!testTweetText.trim()) {
      toast.error(t('tweetTextRequired') || 'Le texte du tweet est requis');
      return;
    }
    
    // P0 FIX: Validate 280 char limit
    if (testTweetText.length > 280) {
      toast.error(`Le tweet dépasse 280 caractères (${testTweetText.length}/280)`);
      return;
    }
    
    setXTesting(true);
    try {
      // P0 FIX: Use correct endpoint /integrations/x/test_post with project_id
      const response = await apiClient.post('/integrations/x/test_post', { 
        project_id: projectId,
        text: testTweetText 
      });
      
      if (response.data.ok) {
        const tweetId = response.data.tweet_id;
        toast.success(
          <div>
            <strong>{t('tweetPosted') || 'Tweet publié avec succès !'}</strong>
            {tweetId && <div className="text-sm opacity-80 mt-1">ID: {tweetId}</div>}
          </div>
        );
      } else {
        // Handle structured error
        const error = response.data;
        let errorMsg = error.message || 'Erreur inconnue';
        
        if (error.action_required === 'REAUTH') {
          errorMsg = 'Session expirée. Veuillez reconnecter X.';
        } else if (error.action_required === 'RATE_LIMIT') {
          errorMsg = 'Limite atteinte. Réessayez plus tard.';
        }
        
        toast.error(
          <div>
            <strong>Erreur</strong>
            <div className="text-sm opacity-80 mt-1">{errorMsg}</div>
            {error.request_id && <div className="text-xs opacity-60">ref: {error.request_id}</div>}
          </div>
        );
      }
    } catch (error) {
      const errorData = error.response?.data || {};
      const httpStatus = error.response?.status;
      const requestId = errorData.request_id;
      const errorCode = errorData.error_code;
      
      // P0 FIX: Handle 405 METHOD_NOT_ALLOWED explicitly
      if (httpStatus === 405 || errorCode === 'HTTP_405' || errorData.error === 'METHOD_NOT_ALLOWED') {
        console.error('[ProjectSettings] ⚠️ 405 METHOD_NOT_ALLOWED detected', {
          request_id: requestId,
          expected_method: errorData.expected_method,
          client_hint: errorData.client_hint
        });
        
        toast.error(
          <div>
            <strong>Bug client: méthode incorrecte (POST requis)</strong>
            <br />
            <span className="text-sm opacity-80">{errorData.client_hint || 'Vérifiez que le bouton utilise fetch POST'}</span>
            {requestId && <div className="text-xs opacity-60 mt-1">ref: {requestId}</div>}
          </div>,
          { duration: 8000 }
        );
        return;
      }
      
      let errorMsg = formatP0Error(error) || 'Erreur lors de la publication';
      
      // Handle structured errors from backend
      if (errorData.error === 'MISSING_INTEGRATION') {
        errorMsg = 'X non configuré. Connectez votre compte dans les paramètres.';
      } else if (errorData.action_required === 'REAUTH') {
        errorMsg = 'Session X expirée. Veuillez reconnecter.';
      }
      
      toast.error(errorMsg);
    } finally {
      setXTesting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('copied') || 'Copié !');
  };

  // ===== TELEGRAM FUNCTIONS =====
  const fetchTelegramStatus = async () => {
    try {
      // P0 HARDENING: Use authGet with proper error classification
      const response = await authGet('/integrations/telegram/status');
      setTgStatus(response.data);
      if (response.data.has_credentials) {
        setTgChatId(response.data.chat_id || '');
        setTgParseMode(response.data.parse_mode || 'HTML');
        setTgDisablePreview(response.data.disable_web_page_preview || false);
        setTgEnabled(response.data.is_enabled || false);
      }
    } catch (error) {
      // P0 DEFINITIVE: Classify error properly
      const classified = classifyApiError(error);
      if (classified.type === 'AME_AUTH') {
        console.warn('[fetchTelegramStatus] AME auth required:', classified.message);
      } else {
        console.error('Failed to fetch Telegram status:', error);
      }
    } finally {
      setTgLoading(false);
    }
  };

  const handleSaveTelegramCredentials = async () => {
    if (!tgBotToken && !tgStatus?.has_credentials) {
      toast.error('Bot Token est requis');
      return;
    }
    if (!tgChatId) {
      toast.error('Chat ID est requis');
      return;
    }
    setTgSaving(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/integrations/telegram', {
        bot_token: tgBotToken || 'KEEP_EXISTING',
        chat_id: tgChatId,
        parse_mode: tgParseMode,
        disable_web_page_preview: tgDisablePreview,
        is_enabled: tgEnabled
      });
      if (response.data.success) {
        toast.success('Configuration Telegram sauvegardée !');
        setTgBotToken('');
        fetchTelegramStatus();
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la sauvegarde');
    } finally {
      setTgSaving(false);
    }
  };

  const handleTestTelegram = async () => {
    setTgTesting(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/integrations/telegram/test', {
        message: 'TTT test ✅ - AI Marketing Engine'
      });
      if (response.data.success) {
        toast.success('Message de test envoyé !');
      } else {
        toast.error(response.data.error || 'Échec du test');
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors du test');
    } finally {
      setTgTesting(false);
    }
  };

  const handleSendSampleTelegram = async () => {
    setTgTesting(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/integrations/telegram/sample-post', {});
      if (response.data.success) {
        toast.success('Post exemple envoyé !');
      } else {
        toast.error(response.data.error || 'Échec de l\'envoi');
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de l\'envoi');
    } finally {
      setTgTesting(false);
    }
  };

  const handleFetchTelegramChats = async () => {
    setTgFetchingChats(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/integrations/telegram/fetch-chat-id', {});
      if (response.data.success && response.data.chats?.length > 0) {
        setTgAvailableChats(response.data.chats);
        toast.success(`${response.data.chats.length} chat(s) trouvé(s)`);
      } else {
        toast.info(response.data.hint || 'Aucun chat trouvé. Ajoutez le bot au canal et envoyez un message.');
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la recherche');
    } finally {
      setTgFetchingChats(false);
    }
  };

  const handleDeleteTelegramCredentials = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer votre intégration Telegram ?')) return;
    try {
      // P0 HARDENING: Use centralized apiClient
      await apiClient.delete('/integrations/telegram');
      toast.success('Intégration Telegram supprimée');
      setTgStatus(null);
      setTgBotToken('');
      setTgChatId('');
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la suppression');
    }
  };

  // ===== FACEBOOK FUNCTIONS =====
  
  // Fetch diagnostic info from /api/social/facebook/diag
  const fetchFacebookDiag = async () => {
    try {
      // P0 HARDENING: Use authGet with proper error classification
      const response = await authGet('/social/facebook/diag');
      setFbDiag(response.data);
      console.log('[FB Diag]', response.data);
      
      // Also update last test info if present
      if (response.data.last_publish) {
        setFbLastTest(response.data.last_publish);
      }
      
      return response.data;
    } catch (error) {
      // P0 DEFINITIVE: Classify error properly
      const classified = classifyApiError(error);
      if (classified.type !== 'AME_AUTH') {
        console.error('Failed to fetch Facebook diag:', error);
      }
      return null;
    }
  };
  
  // Start polling diag every 10s for 60s
  const startFbPolling = () => {
    fbPollingCountRef.current = 0;
    setFbPollingActive(true);
    
    // Clear any existing interval
    if (fbPollingRef.current) {
      clearInterval(fbPollingRef.current);
    }
    
    // Poll every 10 seconds
    fbPollingRef.current = setInterval(async () => {
      fbPollingCountRef.current += 1;
      console.log(`[FB Poll] #${fbPollingCountRef.current}`);
      
      await fetchFacebookDiag();
      await fetchFacebookStatus();
      
      // Stop after 6 polls (60 seconds)
      if (fbPollingCountRef.current >= 6) {
        clearInterval(fbPollingRef.current);
        setFbPollingActive(false);
        console.log('[FB Poll] Stopped');
      }
    }, 10000);
  };
  
  // Cleanup polling on unmount
  React.useEffect(() => {
    return () => {
      if (fbPollingRef.current) {
        clearInterval(fbPollingRef.current);
      }
    };
  }, []);
  
  const fetchFacebookStatus = async () => {
    try {
      // P0 HARDENING: Use authGet with proper error classification
      const response = await authGet('/integrations/facebook/status');
      setFbStatus(response.data);
      if (response.data.default_hashtags) {
        setFbDefaultHashtags(response.data.default_hashtags);
      }
      setFbEnabled(response.data.is_enabled || false);
      setFbAutoPost(response.data.auto_post_enabled || false);
      
      // Multi-tenant fields
      setFbUseCustomApp(response.data.use_custom_app || false);
      setFbHasDefaultApp(response.data.has_default_app || false);
      setFbAppConfigSource(response.data.app_config_source || 'NONE');
      
      // Also fetch diag
      await fetchFacebookDiag();
    } catch (error) {
      // P0 DEFINITIVE: Classify error properly
      const classified = classifyApiError(error);
      if (classified.type !== 'AME_AUTH') {
        console.error('Failed to fetch Facebook status:', error);
      }
    } finally {
      setFbLoading(false);
    }
  };

  // Save Facebook App config (multi-tenant)
  const handleSaveFacebookConfig = async () => {
    if (fbUseCustomApp && !fbAppId) {
      toast.error('App ID est requis quand "Utiliser ma propre app" est activé');
      return;
    }
    if (fbUseCustomApp && !fbAppSecret && !fbStatus?.has_custom_app_secret) {
      toast.error('App Secret est requis pour la première configuration');
      return;
    }
    setFbSaving(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/social/facebook/config', {
        use_custom_app: fbUseCustomApp,
        app_id: fbUseCustomApp ? fbAppId : null,
        app_secret: fbUseCustomApp ? (fbAppSecret || null) : null
      });
      if (response.data.success) {
        toast.success('Configuration Facebook sauvegardée !');
        setFbAppSecret('');  // Clear secret after save for security
        setFbAppConfigSource(response.data.app_config_source);
        fetchFacebookStatus();
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la sauvegarde');
    } finally {
      setFbSaving(false);
    }
  };

  const handleSaveFacebookCredentials = async () => {
    if (!fbAppId && !fbStatus?.has_credentials) {
      toast.error('App ID est requis');
      return;
    }
    if (!fbAppSecret && !fbStatus?.has_credentials) {
      toast.error('App Secret est requis');
      return;
    }
    setFbSaving(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/integrations/facebook', {
        app_id: fbAppId || 'KEEP_EXISTING',
        app_secret: fbAppSecret || 'KEEP_EXISTING',
        default_hashtags: fbDefaultHashtags,
        is_enabled: fbEnabled,
        auto_post_enabled: fbAutoPost,
        auto_post_mode: 'ON_PUBLISH'
      });
      if (response.data.success) {
        toast.success('Configuration Facebook sauvegardée !');
        setFbAppId('');
        setFbAppSecret('');
        fetchFacebookStatus();
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la sauvegarde');
    } finally {
      setFbSaving(false);
    }
  };

  const handleConnectFacebook = async () => {
    // Guardrail: Check if already connected (has user token)
    if (fbDiag?.has_user_token || fbDiag?.status === 'NEEDS_PAGE_SELECTION') {
      toast.info('Déjà connecté. Sélectionnez une page ou testez la publication.', { duration: 4000 });
      return;
    }
    
    setFbConnecting(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.get('/auth/facebook/connect');
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la connexion');
      setFbConnecting(false);
    }
  };

  const handleFetchFacebookPages = async () => {
    setFbLoadingPages(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.get('/social/facebook/pages');
      if (response.data.success) {
        setFbPages(response.data.pages || []);
        if (response.data.pages?.length === 0) {
          const hints = response.data.reason_hints || [];
          let message = 'Aucune page trouvée.';
          if (hints.includes('user_not_admin_of_page')) {
            message += ' Vérifiez que vous êtes administrateur de la Page.';
          }
          if (hints.includes('app_not_in_roles')) {
            message += ' Ajoutez l\'app aux rôles de la Page dans les paramètres Facebook.';
          }
          toast.warning(message, { duration: 6000 });
        }
      } else {
        toast.error(response.data.message || response.data.error || 'Erreur lors de la récupération des pages');
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur');
    } finally {
      setFbLoadingPages(false);
    }
  };

  const handleSelectFacebookPage = async (pageId) => {
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/social/facebook/select-page', { page_id: pageId });
      if (response.data.status === 'READY') {
        toast.success(`Page "${response.data.page_name}" connectée !`);
        setFbPages([]);
        setFbPageModalOpen(false);
        fetchFacebookStatus();
        startFbPolling();  // Start polling after action
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la sélection');
    }
  };

  const handleTestFacebookPost = async () => {
    setFbTesting(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/social/facebook/test', {});
      if (response.data.success) {
        toast.success(`Test publié ! ${response.data.post_url ? '' : ''}`);
        setFbLastTest({
          status: 'SUCCESS',
          at: new Date().toISOString(),
          post_id: response.data.post_id,
          post_url: response.data.post_url
        });
        startFbPolling();  // Start polling after test
      } else if (response.data.needs_reconnect) {
        toast.error('Token expiré. Déconnectez et reconnectez Facebook.');
        setFbLastTest({
          status: 'FAIL',
          at: new Date().toISOString(),
          error: 'Token expiré'
        });
      } else {
        toast.error(response.data.error || 'Erreur lors du test');
        setFbLastTest({
          status: 'FAIL',
          at: new Date().toISOString(),
          error: response.data.error
        });
      }
      fetchFacebookDiag();  // Refresh diag immediately
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur');
      setFbLastTest({
        status: 'FAIL',
        at: new Date().toISOString(),
        error: formatP0Error(error) || 'Erreur'
      });
    } finally {
      setFbTesting(false);
    }
  };

  const handleDisconnectFacebook = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir déconnecter Facebook ?')) return;
    try {
      // P0 HARDENING: Use centralized apiClient
      await apiClient.delete('/social/facebook/disconnect');
      toast.success('Facebook déconnecté');
      setFbPages([]);
      fetchFacebookStatus();
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur');
    }
  };

  const handleDeleteFacebookCredentials = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer votre intégration Facebook ?')) return;
    try {
      // P0 HARDENING: Use centralized apiClient
      await apiClient.delete('/integrations/facebook');
      toast.success('Intégration Facebook supprimée');
      setFbStatus(null);
      setFbDiag(null);
      setFbAppId('');
      setFbAppSecret('');
      setFbPages([]);
      setFbLastTest(null);
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la suppression');
    }
  };

  // Open page selection modal
  const handleOpenPageSelector = async () => {
    setFbPageModalOpen(true);
    setFbLoadingPages(true);
    await handleFetchFacebookPages();
  };

  // Format date for display
  const formatFbDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Get Facebook status badge
  const getFbStatusBadge = () => {
    if (!fbDiag) return { color: 'gray', text: 'Chargement...', icon: '⏳' };
    
    switch (fbDiag.status) {
      case 'READY':
        return { color: 'green', text: `Connecté (${fbDiag.selected_page || 'Prêt à publier'})`, icon: '✅' };
      case 'NEEDS_PAGE_SELECTION':
        // Important: Ne jamais afficher "Non connecté" si OAuth est fait
        return { color: 'yellow', text: 'Connecté - Sélectionnez une page', icon: '📄' };
      case 'NO_PAGES_FOUND':
        return { color: 'orange', text: 'OAuth OK - Aucune page trouvée', icon: '⚠️' };
      case 'NEEDS_OAUTH':
      case 'NOT_CONFIGURED':
        return { color: 'red', text: 'Non connecté', icon: '❌' };
      case 'NEEDS_APP_CONFIG':
        return { color: 'red', text: 'Configuration requise', icon: '⚙️' };
      case 'TOKEN_EXPIRED':
        return { color: 'red', text: 'Token expiré - Reconnexion requise', icon: '⏰' };
      case 'TOKEN_EXPIRING_SOON':
        return { color: 'orange', text: `Token expire dans ${fbDiag.days_until_expiry}j`, icon: '⚠️' };
      case 'DISABLED':
        return { color: 'gray', text: 'Désactivé', icon: '⏸️' };
      default:
        // Check intermediate states
        if (fbDiag.oauth_status === 'DONE' && !fbDiag.has_page_token) {
          return { color: 'yellow', text: 'OAuth OK - Action requise', icon: '⚠️' };
        }
        if (fbDiag.has_user_token && fbDiag.pages_count > 0) {
          return { color: 'yellow', text: 'Connecté - Sélection requise', icon: '📄' };
        }
        if (fbDiag.last_graph_error) {
          return { color: 'red', text: 'Erreur Graph API', icon: '❌' };
        }
        return { color: 'gray', text: fbDiag.status || 'Inconnu', icon: '❓' };
    }
  };

  // ===== INSTAGRAM INTEGRATION FUNCTIONS =====
  
  const fetchInstagramStatus = async () => {
    // P0 FIX: Validate projectId before making API calls
    if (!projectId) {
      console.warn('[fetchInstagramStatus] No projectId available');
      setIgLoading(false);
      return;
    }
    
    try {
      // P0 HARDENING: Use projectGet with automatic header injection
      const diagResponse = await projectGet('/social/instagram/diag', projectId);
      setIgDiag(diagResponse.data);
      setIgStatus(diagResponse.data);
      
      if (diagResponse.data.has_custom_app_id !== undefined) {
        setIgUseCustomApp(diagResponse.data.has_custom_app_id);
      }
      if (diagResponse.data.is_enabled !== undefined) {
        setIgEnabled(diagResponse.data.is_enabled);
      }
      
      // Fetch unified status for POST_READY logic
      try {
        const statusResponse = await projectGet('/social/instagram/status', projectId);
        setIgUnifiedStatus(statusResponse.data);
        
        // P0 HYSTERESIS: Mettre à jour IntegrationStateManager sur succès
        if (statusResponse.data.publish_ready || statusResponse.data.blocking_reason === 'POST_READY') {
          IntegrationStateManager.setState('OK');
        }
      } catch (statusError) {
        // P0 PRODUCTION-GRADE: Status poll failures should NEVER show a toast
        // They should only update state silently - user will see indicator in UI
        // NE PAS mettre à jour IntegrationStateManager ici (conserver lastKnownGood)
        const classified = classifyApiError(statusError);
        console.warn('[fetchInstagramStatus] Status poll failed (silent):', classified.type, classified.errorCode);
        
        // Set appropriate state WITHOUT showing toast
        if (classified.type === 'AME_AUTH') {
          setIgUnifiedStatus({
            ui_state: 'ame_auth_required',
            blocking_reason: 'AME_AUTH_REQUIRED',
            blocking_action: 'AME_LOGIN',
            error_message: classified.message,
            publish_ready: false
          });
        } else if (classified.type === 'NETWORK' || classified.type === 'META_UNAVAILABLE') {
          setIgUnifiedStatus({
            ui_state: 'meta_unavailable',
            blocking_reason: 'META_UNAVAILABLE',
            blocking_action: 'RETRY',
            error_message: classified.message,
            publish_ready: false
          });
        }
        // NO TOAST - this is an automatic poll, not a user action
      }
    } catch (error) {
      // P0 PRODUCTION-GRADE: Diag poll failures should NEVER show a toast
      // They should only update state silently - user will see indicator in UI
      const classified = classifyApiError(error);
      console.warn('[fetchInstagramStatus] Diag poll failed (silent):', classified.type, classified.errorCode);
      
      // P0 CRITICAL: Set state silently - NO TOAST for automatic polls
      if (classified.type === 'AME_AUTH') {
        // Set state indicating auth issue, not Meta issue - NO TOAST
        setIgDiag({
          status: 'AME_AUTH_REQUIRED',
          error_type: 'AME_AUTH',
          message: classified.message
        });
      } else {
        // For other errors, set a generic error state - NO TOAST
        const errorCode = error.response?.data?.detail?.error_code || error.response?.data?.error_code;
        setIgDiag({
          status: 'POLL_FAILED',
          error_type: classified.type || 'UNKNOWN',
          error_code: errorCode,
          message: classified.message || 'Status poll failed'
        });
      }
      // NO TOAST - this is an automatic poll, not a user action
    } finally {
      setIgLoading(false);
    }
  };

  const handleSaveInstagramConfig = async () => {
    if (igUseCustomApp && !igAppId && !igDiag?.has_custom_app_id) {
      toast.error('App ID requis');
      return;
    }
    
    // P0 FIX: Validate projectId
    if (!projectId) {
      toast.error('⚠️ PROJECT_ID_REQUIRED: Sélectionnez un projet');
      return;
    }
    
    setIgSaving(true);
    try {
      // P0 HARDENING: Use projectPost with automatic header injection
      await projectPost('/social/instagram/config', {
        use_custom_app: igUseCustomApp,
        app_id: igAppId || undefined,
        app_secret: igAppSecret || undefined
      }, projectId);
      
      toast.success('Configuration Instagram sauvegardée');
      setIgAppSecret('');
      fetchInstagramStatus();
    } catch (error) {
      // P0 FIX: Show real error code
      const errorMsg = formatP0Error(error);
      toast.error(errorMsg);
    } finally {
      setIgSaving(false);
    }
  };

  const handleInstagramConnect = async () => {
    setIgConnecting(true);
    setIgError(null);
    
    // Check if user is authenticated
    if (!token) {
      setIgError({
        error: 'NOT_AUTHENTICATED',
        message: 'Vous devez être connecté pour lier Instagram',
        next_action: 'LOGIN'
      });
      toast.error('Authentification requise. Veuillez vous connecter.');
      setIgConnecting(false);
      return;
    }
    
    // P0 PRODUCTION-GRADE: Validate projectId before API call
    const normalizedProjectId = normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      setIgError({
        error: 'PROJECT_ID_REQUIRED',
        message: 'Sélectionnez un projet avant de connecter Instagram',
        next_action: 'SELECT_PROJECT'
      });
      toast.error(
        <div className="space-y-1">
          <div className="font-semibold">Projet non sélectionné</div>
          <div className="text-xs opacity-80">Code: PROJECT_ID_REQUIRED | Stage: CONTEXT_RESOLVE</div>
        </div>,
        { duration: 6000 }
      );
      setIgConnecting(false);
      return;
    }
    
    try {
      console.info('[Instagram Connect] Calling API to get OAuth URL...');
      console.info('[Instagram Connect] API_URL:', API_URL, '| projectId:', normalizedProjectId);
      
      // P0 HARDENING: Use projectGet with automatic header injection
      const response = await projectGet('/auth/instagram/connect', normalizedProjectId);
      
      const requestId = response.data?.request_id || response.headers?.['x-request-id'] || 'unknown';
      console.info('[Instagram Connect] Response received, request_id:', requestId);
      
      // Backend ALWAYS returns JSON with auth_url on success
      if (response.data?.auth_url) {
        console.info('[Instagram Connect] Got auth_url, redirecting to Facebook OAuth...');
        window.location.href = response.data.auth_url;
        // Don't setIgConnecting(false) - we're navigating away
        return;
      }
      
      // No auth_url in response - this shouldn't happen with the new backend
      console.error('[Instagram Connect] No auth_url in response:', response.data);
      setIgError({
        error: response.data?.error || 'UNEXPECTED_RESPONSE',
        message: response.data?.message || 'Réponse inattendue du serveur. Réessayez.',
        request_id: requestId
      });
      toast.error(`Erreur: ${response.data?.message || 'Réponse inattendue'} (${requestId})`);
      setIgConnecting(false);
      
    } catch (error) {
      const errorData = error.response?.data || {};
      const requestId = errorData.request_id || error.response?.headers?.['x-request-id'] || 'unknown';
      const status = error.response?.status;
      
      console.error('[Instagram Connect] Error:', { status, errorData, requestId });
      
      // Handle specific error cases
      if (status === 401 || status === 403 || errorData.next_action === 'LOGIN') {
        setIgError({
          error: 'NOT_AUTHENTICATED',
          message: errorData.message || 'Session expirée. Veuillez vous reconnecter.',
          request_id: requestId,
          next_action: 'LOGIN'
        });
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else if (status === 400 && (errorData.error === 'WRONG_APP_ID' || errorData.error === 'INVALID_APP_ID')) {
        // User configured Instagram App ID instead of Facebook App ID
        setIgError({
          error: errorData.error,
          message: errorData.message,
          hint: errorData.hint,
          configured_id: errorData.configured_id,
          expected_id: errorData.expected_id,
          request_id: requestId
        });
        toast.error('Mauvais App ID configuré. Voir les détails ci-dessous.');
      } else if (status === 400 && errorData.error === 'MISCONFIGURED') {
        setIgError({
          error: 'MISCONFIGURED',
          message: errorData.message || 'App Meta non configurée',
          hint: errorData.hint,
          request_id: requestId
        });
        toast.error(errorData.message || 'Configurez d\'abord votre App Meta');
      } else {
        setIgError({
          error: errorData.error || 'CONNECTION_ERROR',
          message: errorData.message || 'Erreur lors de la connexion Instagram',
          hint: errorData.hint,
          request_id: requestId
        });
        
        // Map error codes to clear user messages (no generic "Connexion impossible")
        const errorMessages = {
          'INVALID_APP_ID': 'ID d\'application invalide',
          'WRONG_APP_ID': 'Mauvais App ID (utilisez le Facebook App ID)',
          'INVALID_APP_ID_FORMAT': 'Format d\'App ID invalide',
          'MISCONFIGURED': 'App Meta non configurée',
          'NOT_CONFIGURED': 'Configuration Instagram non trouvée',
          'INVALID_SCOPES': 'Permissions OAuth invalides',
          'TOKEN_ERROR': 'Erreur de token',
          'GRAPH_ERROR': 'Erreur Graph API Meta'
        };
        
        const friendlyMessage = errorMessages[errorData.error] || errorData.message || 'Erreur OAuth';
        toast.error(`${friendlyMessage} (${requestId})`);
      }
      
      setIgConnecting(false);
    }
  };

  const handleInstagramTest = async () => {
    // P0 FIX: Verify project_id is present and valid before making request
    const normalizedProjectId = normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      toast.error(
        <div className="space-y-1">
          <div className="font-semibold">Projet non sélectionné</div>
          <div className="text-xs opacity-80">
            Code: PROJECT_ID_REQUIRED | Stage: CONTEXT_RESOLVE
          </div>
          <div className="text-xs mt-1">
            💡 Retournez au tableau de bord et sélectionnez un projet
          </div>
        </div>,
        { duration: 8000 }
      );
      return;
    }
    
    setIgTesting(true);
    try {
      // P0 HARDENING: Use projectPost with automatic header injection
      const response = await projectPost('/social/instagram/test', {}, normalizedProjectId);
      
      if (response.data.success) {
        toast.success(`✅ Test publié sur @${igDiag?.ig_username || 'Instagram'} !`);
        // Show request_id for traceability
        if (response.data.request_id) {
          console.log('[TEST_PUBLISH] Success request_id:', response.data.request_id);
        }
        // Succès → mettre à jour IntegrationStateManager
        IntegrationStateManager.setState('OK');
      } else {
        // P0 PRODUCTION-GRADE: Display structured error with request_id
        const errCode = response.data.error_code || response.data.error || 'UNKNOWN_ERROR';
        const errStage = response.data.stage || 'UNKNOWN';
        const reqId = response.data.request_id || 'N/A';
        const errMsg = response.data.message || response.data.error || 'Échec du test';
        const scopeRes = response.data.debug_info?.scope_resolution;
        
        toast.error(
          <div className="space-y-1">
            <div className="font-semibold">{errMsg}</div>
            <div className="text-xs opacity-80">
              Code: {errCode} | Stage: {errStage}
            </div>
            {scopeRes && (
              <div className="text-xs opacity-70">
                Confidence: {scopeRes.confidence} | Token: {scopeRes.token_type_detected}
              </div>
            )}
            <div className="text-xs font-mono opacity-60">
              ID: {reqId}
            </div>
          </div>,
          { duration: 8000 }
        );
        
        console.error('[TEST_PUBLISH] Error:', response.data);
      }
      fetchInstagramStatus();
    } catch (error) {
      // P0 PRODUCTION-GRADE: Gestionnaire centralisé basé sur signaux backend
      // Logique STRICTE: is_actionable, transient_hint, error_reason
      handleActionError(error, toast, { 
        context: 'TEST_PUBLISH'
      });
      console.error('[TEST_PUBLISH] Error:', error.response?.data || error.message);
    } finally {
      setIgTesting(false);
    }
  };

  // Check if Instagram disconnect button should be visible
  // Show when: user_token_ok == true OR pages_count > 0 OR selected_page_id != null
  const shouldShowIgDisconnect = () => {
    return (
      igUnifiedStatus?.user_token_ok === true ||
      igUnifiedStatus?.pages_count > 0 ||
      igUnifiedStatus?.selected_page_id ||
      igDiag?.oauth_connected ||
      igDiag?.pages_count > 0 ||
      igDiag?.selected_ig_id ||
      igDiag?.status === 'POST_READY' ||
      igDiag?.status === 'CONNECTED' ||
      igDiag?.status === 'OAUTH_DONE'
    );
  };

  // ============================================================================
  // GARANTIE #4: Frontend "Full Reset" - Clear ALL local state
  // Includes: localStorage, sessionStorage, IndexedDB, CacheStorage, ServiceWorkers
  // ============================================================================
  const clearAllLocalState = async () => {
    const cleared = {
      localStorage: 0,
      sessionStorage: false,
      indexedDB: 0,
      cacheStorage: 0,
      serviceWorkers: 0
    };
    
    try {
      // 1. Clear localStorage - both full clear and targeted removal
      const keysToRemove = [];
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && (
          key.startsWith('ig_') ||
          key.startsWith('instagram_') ||
          key.startsWith('fb_') ||
          key.startsWith('facebook_') ||
          key.startsWith('oauth_') ||
          key.startsWith('ame_') ||
          key.startsWith('project_settings_') ||
          key.includes('selected_page') ||
          key.includes('publish_ready') ||
          key.includes('token') ||
          key.includes('cache') ||
          key.includes('credential')
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      cleared.localStorage = keysToRemove.length;
      
      // 2. Clear sessionStorage completely
      sessionStorage.clear();
      cleared.sessionStorage = true;
      
      // 3. Purge IndexedDB (all known databases + discovery)
      if ('indexedDB' in window) {
        try {
          // Try to get all database names (Chrome/Edge)
          if (indexedDB.databases) {
            const dbs = await indexedDB.databases();
            for (const db of dbs) {
              if (db.name) {
                try {
                  indexedDB.deleteDatabase(db.name);
                  cleared.indexedDB++;
                } catch (e) {
                  console.warn('[Hard Disconnect] IndexedDB delete failed:', db.name);
                }
              }
            }
          }
          
          // Also try known database names
          const knownDBs = ['ame-cache', 'instagram-cache', 'oauth-state', 'project-data'];
          for (const dbName of knownDBs) {
            try {
              indexedDB.deleteDatabase(dbName);
            } catch (e) {
              // Ignore
            }
          }
        } catch (e) {
          console.warn('[Hard Disconnect] IndexedDB cleanup error:', e);
        }
      }
      
      // 4. Purge CacheStorage (all caches)
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
          cleared.cacheStorage = cacheNames.length;
        } catch (e) {
          console.warn('[Hard Disconnect] CacheStorage cleanup error:', e);
        }
      }
      
      // 5. Unregister all ServiceWorkers
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const registration of registrations) {
            await registration.unregister();
            cleared.serviceWorkers++;
          }
        } catch (e) {
          console.warn('[Hard Disconnect] ServiceWorker unregister error:', e);
        }
      }
      
      console.info('[Instagram Hard Disconnect] Full reset completed:', cleared);
      
    } catch (e) {
      console.error('[Instagram Hard Disconnect] clearAllLocalState error:', e);
    }
    
    return cleared;
  };

  const handleInstagramDisconnect = async () => {
    // P0 PRODUCTION-GRADE: Validate projectId before API call
    const normalizedProjectId = normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      toast.error(
        <div className="space-y-1">
          <div className="font-semibold">Projet non sélectionné</div>
          <div className="text-xs opacity-80">Code: PROJECT_ID_REQUIRED | Stage: CONTEXT_RESOLVE</div>
        </div>,
        { duration: 6000 }
      );
      return;
    }
    
    setIgDisconnecting(true);
    
    try {
      // ========================================================================
      // HARD DISCONNECT - Production Grade (5 Garanties)
      // ========================================================================
      // GARANTIE #1: Backend ordre strict (Meta revoke avant purge)
      // GARANTIE #2: Backend preuve invalidation (2 checks)
      // GARANTIE #3: Backend réponse explicite (HTTP 200/409/429)
      // GARANTIE #4: Frontend Full Reset (ci-dessous)
      // GARANTIE #5: Backend rate limit + logs structurés
      // ========================================================================
      
      // P0 HARDENING: Use projectPost with automatic header injection
      const response = await projectPost('/social/instagram/disconnect-hard', {}, normalizedProjectId);
      
      const data = response.data || {};
      const requestId = data.request_id || response.headers?.['x-request-id'] || 'unknown';
      
      // HTTP 200 = Success (token invalidation verified + local purge OK)
      if (data.ok && data.token_invalidation_verified) {
        // Show detailed success message
        const stepsCount = data.steps_completed?.length || 0;
        const clearedCount = data.local_data_cleared?.length || 0;
        const oauthCleared = data.oauth_states_cleared || 0;
        
        toast.success('✅ Déconnexion Instagram COMPLÈTE', { 
          duration: 4000,
          description: `Token révoqué & vérifié. ${stepsCount} étapes, ${clearedCount} collections, ${oauthCleared} oauth states.`
        });
        
        // GARANTIE #4: Full local state reset (async - includes IndexedDB, SW, etc.)
        await clearAllLocalState();
        
        // Full page reload with cache bypass + disconnect indicator
        setTimeout(() => {
          const baseUrl = window.location.pathname;
          window.location.href = baseUrl + '?reset=' + Date.now();
        }, 1500);
        
      } else {
        // Unexpected state - should not happen with HTTP 200
        toast.warning(data.error_message || '⚠️ État inattendu.', { 
          duration: 5000,
          description: `Request ID: ${requestId}`
        });
        
        await clearAllLocalState();
        setTimeout(() => {
          window.location.href = window.location.pathname + '?reset=' + Date.now();
        }, 2000);
      }
      
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data?.detail || error.response?.data || {};
      const requestId = data.request_id || error.response?.headers?.['x-request-id'] || 'unknown';
      
      // Handle 401/403 - session expired
      if (status === 401 || status === 403) {
        toast.error('Session expirée. Veuillez vous reconnecter.', {
          description: `Request ID: ${requestId}`,
          duration: 6000,
          action: {
            label: 'Connexion',
            onClick: () => navigate('/auth')
          }
        });
        setIgDisconnecting(false);
        return;
      }
      
      // Handle 429 - rate limited (GARANTIE #5)
      if (status === 429) {
        const retryAfter = data.retry_after_seconds || 30;
        toast.error(`⏳ Trop de tentatives. Réessayez dans ${retryAfter}s.`, {
          description: `Request ID: ${requestId}`,
          duration: retryAfter * 1000
        });
        setIgDisconnecting(false);
        return;
      }
      
      // Handle 409 - incomplete disconnect OR in progress (GARANTIE #3)
      if (status === 409) {
        const errorCode = data.error_code || 'HARD_DISCONNECT_INCOMPLETE';
        const retryAfter = data.retry_after_seconds || 5;
        
        // Different handling based on error code
        if (errorCode === 'DISCONNECT_IN_PROGRESS') {
          toast.warning(`⏳ Déconnexion déjà en cours. Réessayez dans ${retryAfter}s.`, {
            description: `Request ID: ${requestId}`,
            duration: retryAfter * 1000 + 2000
          });
        } else {
          // HARD_DISCONNECT_INCOMPLETE - token still valid
          let description = `Code: ${errorCode} | Request ID: ${requestId}`;
          if (data.invalidation_check) {
            const ic = data.invalidation_check;
            description += ` | /me: ${ic.me_check} | debug: ${ic.debug_token_check}`;
          }
          
          toast.error(data.error_message || '⚠️ Déconnexion partielle - Token potentiellement encore valide.', {
            description: description,
            duration: 8000,
            action: {
              label: `Réessayer (${retryAfter}s)`,
              onClick: () => {
                setTimeout(() => handleInstagramDisconnect(), retryAfter * 1000);
              }
            }
          });
        }
        
        setIgDisconnecting(false);
        return;
      }
      
      // Handle 503 - invariant failure (state inconsistency)
      if (status === 503) {
        const errorCode = data.error_code || 'DISCONNECT_INVARIANT_FAILED';
        const invariantChecks = data.invariant_checks || {};
        
        let description = `Code: ${errorCode} | Request ID: ${requestId}`;
        if (invariantChecks.residual_fields?.length > 0) {
          description += ` | Champs résiduels: ${invariantChecks.residual_fields.slice(0, 3).join(', ')}`;
        }
        
        toast.error('❌ État incohérent détecté après déconnexion.', {
          description: description,
          duration: 10000,
          action: {
            label: 'Réessayer',
            onClick: () => handleInstagramDisconnect()
          }
        });
        
        setIgDisconnecting(false);
        return;
      }
      
      // Handle other errors
      const errorMessage = data.error_message || data.message || 
                          error.response?.data?.detail || 
                          'Erreur lors de la déconnexion';
      toast.error(errorMessage, {
        description: `Request ID: ${requestId}`,
        duration: 5000
      });
      
      setIgDisconnecting(false);
    }
    // Note: Don't setIgDisconnecting(false) on success - page will reload
  };

  const fetchInstagramAccounts = async () => {
    // P0 PRODUCTION-GRADE: Validate projectId before API call
    const normalizedProjectId = normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      console.warn('[fetchInstagramAccounts] No projectId - skipping');
      return;
    }
    
    setIgLoadingAccounts(true);
    try {
      // P0 HARDENING: Use projectGet with automatic header injection
      const response = await projectGet('/social/instagram/accounts', normalizedProjectId);
      // Use 'pages' which contains ALL pages (with and without IG)
      // Fallback to 'accounts' for backwards compatibility
      setIgAccounts(response.data.pages || response.data.accounts || []);
    } catch (error) {
      console.error('Fetch IG accounts error:', error);
      toast.error(formatP0Error(error) || 'Erreur lors de la récupération des comptes');
    } finally {
      setIgLoadingAccounts(false);
    }
  };

  // POINT #4: Force refresh accounts from Facebook API (bypass cache)
  const handleRefreshInstagramAccounts = async () => {
    // P0 PRODUCTION-GRADE: Validate projectId before API call
    const normalizedProjectId = normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      toast.error(
        <div className="space-y-1">
          <div className="font-semibold">Projet non sélectionné</div>
          <div className="text-xs opacity-80">Code: PROJECT_ID_REQUIRED | Stage: CONTEXT_RESOLVE</div>
        </div>,
        { duration: 6000 }
      );
      return;
    }
    
    setIgRefreshing(true);
    try {
      // P0 HARDENING: Use projectPost with automatic header injection
      const response = await projectPost('/social/instagram/refresh-accounts', {}, normalizedProjectId);
      
      if (response.data.success || response.data.ok) {
        setIgAccounts(response.data.pages || []);
        toast.success(response.data.message || 'Liste rafraîchie !', { duration: 3000 });
        // Also refresh status
        fetchInstagramStatus();
      } else {
        toast.error(response.data.error || 'Erreur lors du rafraîchissement');
      }
    } catch (error) {
      console.error('Refresh IG accounts error:', error);
      // Handle rate limiting
      if (error.response?.status === 429) {
        const retryAfter = error.response?.data?.retry_after_seconds || 60;
        toast.error(`Veuillez patienter ${retryAfter}s avant de rafraîchir à nouveau.`, { duration: 5000 });
      } else if (error.response?.status === 409) {
        toast.info('Rafraîchissement déjà en cours...', { duration: 3000 });
      } else {
        toast.error(formatP0Error(error) || 'Erreur lors du rafraîchissement');
      }
    } finally {
      setIgRefreshing(false);
    }
  };

  // Discovery Retry - Production Hardened (P0)
  // Uses distributed locking, idempotency, and exponential backoff
  const handleDiscoveryRetry = async (forceReset = false, preferredPageId = null) => {
    if (igDiscoveryRetrying || igDiscoveryLocked || (igDiscoveryRetryAfter > 0 && !forceReset)) {
      return; // Already in progress or rate limited (unless force reset)
    }
    
    // P0 PRODUCTION-GRADE: Validate projectId before API call
    const normalizedProjectId = normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      toast.error(
        <div className="space-y-1">
          <div className="font-semibold">Projet non sélectionné</div>
          <div className="text-xs opacity-80">Code: PROJECT_ID_REQUIRED | Stage: CONTEXT_RESOLVE</div>
        </div>,
        { duration: 6000 }
      );
      return;
    }
    
    setIgDiscoveryRetrying(true);
    try {
      // P0 HARDENING: Use projectPost with automatic header injection
      const response = await projectPost('/social/instagram/discover-retry', { 
        force_reset: forceReset,
        preferred_page_id: preferredPageId 
      }, normalizedProjectId);
      
      const data = response.data;
      
      // Store attempt_id for display
      if (data.attempt_id) {
        setIgLastAttemptId(data.attempt_id);
      }
      if (data.error_source || data.last_error_source) {
        setIgLastErrorSource(data.error_source || data.last_error_source);
      }
      
      if (data.success) {
        if (data.requires_page_selection && data.pages_with_ig?.length > 1) {
          // Multiple pages found - need selection
          toast.info(`📋 ${data.pages_with_ig.length} Pages avec Instagram trouvées. Sélectionnez-en une.`, { duration: 5000 });
          // Store pages for selection modal
          setIgPagesWithIg(data.pages_with_ig);
          setIgPageSelectionModalOpen(true);
        } else {
          toast.success(data.message || 'Découverte réussie !', { duration: 4000 });
        }
        // Refresh status to reflect the new state
        fetchInstagramStatus();
        // Clear any retry countdown
        setIgDiscoveryRetryAfter(null);
        setIgDiscoveryLocked(false);
      } else {
        // Handle different states
        if (data.integration_state === 'RATE_LIMITED') {
          const retryAfter = data.retry_after_seconds || 60;
          setIgDiscoveryRetryAfter(retryAfter);
          toast.warning(`⏳ Trop de tentatives. Réessayez dans ${retryAfter}s.`, { duration: Math.min(retryAfter * 1000, 10000) });
          // Start countdown
          const countdownInterval = setInterval(() => {
            setIgDiscoveryRetryAfter(prev => {
              if (prev <= 1) {
                clearInterval(countdownInterval);
                return null;
              }
              return prev - 1;
            });
          }, 1000);
        } else if (data.integration_state === 'DISCOVERY_IN_PROGRESS' || data.is_locked) {
          setIgDiscoveryLocked(true);
          const retryAfter = data.retry_after_seconds || 30;
          toast.info(`🔒 Découverte déjà en cours. Patientez ${retryAfter}s...`, { duration: 5000 });
          // Auto-unlock after the lock expires
          setTimeout(() => {
            setIgDiscoveryLocked(false);
            fetchInstagramStatus(); // Refresh status
          }, retryAfter * 1000);
        } else if (data.integration_state === 'SELECT_PAGE_REQUIRED') {
          // Multiple pages found
          setIgPagesWithIg(data.pages_with_ig || []);
          setIgPageSelectionModalOpen(true);
          toast.info('📋 Sélectionnez une Page Facebook.', { duration: 4000 });
        } else if (data.integration_state === 'NEEDS_RECONNECT') {
          toast.error('🔑 Token invalide. Veuillez reconnecter Instagram.', { 
            duration: 6000,
            action: {
              label: 'Reconnecter',
              onClick: () => handleInstagramConnect()
            }
          });
        } else {
          // NEEDS_META_ACTION or other
          toast.warning(data.message || 'Découverte incomplète. Vérifiez votre configuration Meta.', { duration: 5000 });
        }
        
        // Refresh status anyway
        fetchInstagramStatus();
      }
    } catch (error) {
      console.error('Discovery retry error:', error);
      if (error.response?.status === 429) {
        const retryAfter = error.response?.data?.retry_after_seconds || 60;
        setIgDiscoveryRetryAfter(retryAfter);
        toast.error(`Trop de tentatives. Patientez ${retryAfter}s.`, { duration: 5000 });
      } else if (error.response?.status === 409) {
        setIgDiscoveryLocked(true);
        toast.info('Découverte déjà en cours...', { duration: 3000 });
      } else if (error.response?.status === 401) {
        toast.error('Session expirée. Veuillez vous reconnecter.', { duration: 5000 });
      } else {
        toast.error(error.response?.data?.message || 'Erreur lors de la découverte', { duration: 5000 });
      }
    } finally {
      setIgDiscoveryRetrying(false);
    }
  };

  // Force retry with backoff reset ("J'ai corrigé, retenter")
  const handleForceDiscoveryRetry = () => {
    handleDiscoveryRetry(true, null);
  };

  // Copy Support Bundle - v2.3
  const handleCopySupportBundle = async () => {
    // P0 PRODUCTION-GRADE: Validate projectId before API call
    const normalizedProjectId = normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      toast.error(
        <div className="space-y-1">
          <div className="font-semibold">Projet non sélectionné</div>
          <div className="text-xs opacity-80">Code: PROJECT_ID_REQUIRED | Stage: CONTEXT_RESOLVE</div>
        </div>,
        { duration: 6000 }
      );
      return;
    }
    
    try {
      // P0 HARDENING: Use projectGet with automatic header injection
      const response = await projectGet('/social/instagram/discovery-support-bundle', normalizedProjectId);
      
      const bundleText = JSON.stringify(response.data, null, 2);
      await navigator.clipboard.writeText(bundleText);
      
      toast.success('📋 Bundle support copié dans le presse-papier !', { duration: 3000 });
    } catch (error) {
      console.error('Copy support bundle error:', error);
      toast.error(formatP0Error(error) || 'Erreur lors de la copie du bundle', { duration: 3000 });
    }
  };

  // App Review Check - Verify prerequisites for Meta App Review submission
  const handleAppReviewCheck = async () => {
    // P0 PRODUCTION-GRADE: Validate projectId before API call
    const normalizedProjectId = normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      toast.error(
        <div className="space-y-1">
          <div className="font-semibold">Projet non sélectionné</div>
          <div className="text-xs opacity-80">Code: PROJECT_ID_REQUIRED | Stage: CONTEXT_RESOLVE</div>
        </div>,
        { duration: 6000 }
      );
      return;
    }
    
    setIgAppReviewLoading(true);
    try {
      // P0 HARDENING: Use projectGet with automatic header injection
      const response = await projectGet('/social/instagram/app-review-check', normalizedProjectId);
      
      setIgAppReviewCheck(response.data);
      setIgAppReviewOpen(true);
      
      if (response.data.ready_for_app_review) {
        toast.success('✅ Prêt pour Meta App Review !', { duration: 5000 });
      } else {
        toast.info(response.data.blocking_message || 'Vérifiez les prérequis ci-dessous.', { duration: 5000 });
      }
    } catch (error) {
      console.error('App Review Check error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expirée. Veuillez vous reconnecter.', { duration: 5000 });
      } else {
        toast.error(formatP0Error(error) || 'Erreur lors de la vérification');
      }
    } finally {
      setIgAppReviewLoading(false);
    }
  };

  // Handle App Review Check actions
  const handleAppReviewAction = (action) => {
    switch (action.action || action.type) {
      case 'CONNECT_OAUTH':
      case 'action':
        if (action.action === 'CONNECT_OAUTH') {
          handleInstagramConnect();
        } else if (action.action === 'SELECT_PAGE') {
          fetchInstagramAccounts();
          setIgAccountModalOpen(true);
        } else if (action.action === 'REFRESH_ACCOUNTS') {
          handleRefreshInstagramAccounts();
        }
        break;
      case 'link':
        if (action.url) {
          window.open(action.url, '_blank', 'noopener,noreferrer');
        }
        break;
      default:
        break;
    }
    setIgAppReviewOpen(false);
  };

  // ==========================================================================
  // INSTAGRAM PROJECT ACTIVATION
  // Enable Instagram for this specific project
  // ==========================================================================
  const [igProjectEnabling, setIgProjectEnabling] = useState(false);
  const [igAutoPublishEnabling, setIgAutoPublishEnabling] = useState(false);

  const handleEnableInstagramForProject = async (withAutoPublish = false) => {
    // P0 PRODUCTION-GRADE: Validate projectId before API call
    const normalizedProjectId = normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      toast.error(
        <div className="space-y-1">
          <div className="font-semibold">Projet non sélectionné</div>
          <div className="text-xs opacity-80">Code: PROJECT_ID_REQUIRED | Stage: CONTEXT_RESOLVE</div>
        </div>,
        { duration: 6000 }
      );
      return;
    }
    
    if (withAutoPublish) {
      setIgAutoPublishEnabling(true);
    } else {
      setIgProjectEnabling(true);
    }
    
    try {
      // P0 HARDENING: Use projectPost with automatic header injection
      const response = await projectPost('/social/instagram/enable-for-project', { 
        auto_publish: withAutoPublish 
      }, normalizedProjectId);
      
      if (response.data.success) {
        toast.success(
          withAutoPublish 
            ? '✅ Instagram activé avec publication automatique !' 
            : '✅ Instagram activé pour ce projet !',
          { duration: 4000 }
        );
        // Refresh status
        fetchInstagramStatus();
      } else {
        toast.error(response.data.message || 'Erreur lors de l\'activation');
      }
    } catch (error) {
      console.error('Enable Instagram error:', error);
      toast.error(formatP0Error(error) || 'Erreur lors de l\'activation');
    } finally {
      setIgProjectEnabling(false);
      setIgAutoPublishEnabling(false);
    }
  };

  // ==========================================================================
  // META DEBUG BUNDLE (P0 - Production Debugging)
  // Visible only when ?debug=1 in URL
  // ==========================================================================
  const fetchMetaDebugBundle = async (traceRefresh = false, tracePublish = true) => {
    setMetaDebugLoading(true);
    try {
      // P0 PRODUCTION-GRADE: Validate projectId and use projectGet for context injection
      const normalizedProjectId = normalizeProjectId(projectId);
      if (!normalizedProjectId) {
        console.warn('[fetchMetaDebugBundle] No projectId - returning CONTEXT_REQUIRED state');
        setMetaDebugBundle({
          integration_state: 'CONTEXT_REQUIRED',
          publish_allowed: false,
          publish_readiness_valid: false,
          project_id: '',
          bundle_id: 'local_no_context',
          message: 'Sélectionnez un projet pour voir le diagnostic'
        });
        return null;
      }
      
      const response = await projectGet('/debug/meta', normalizedProjectId, {
        trace_refresh: traceRefresh,
        trace_publish: tracePublish
      });
      
      const data = response.data;
      
      if (data.ok) {
        setMetaDebugBundle(data.bundle);
        return data.bundle;
      } else {
        toast.error(data.message || 'Erreur lors de la génération du debug bundle');
        return null;
      }
    } catch (error) {
      console.error('Meta Debug Bundle error:', error);
      
      if (error.response?.status === 429) {
        const retryAfter = error.response?.data?.detail?.retry_after_seconds || 60;
        toast.error(`Trop de requêtes. Réessayez dans ${retryAfter}s.`, {
          duration: 5000,
          icon: '⏳'
        });
        return null;
      }
      
      if (error.response?.status === 401) {
        const hint = error.response?.data?.detail?.hint || "Reconnectez-vous pour continuer.";
        toast.error(`Session expirée. ${hint}`, {
          duration: 5000,
          action: {
            label: 'Connexion',
            onClick: () => navigate('/auth')
          }
        });
        return null;
      }
      
      toast.error(formatP0Error(error) || 'Erreur réseau lors de la génération du debug bundle');
      return null;
    } finally {
      setMetaDebugLoading(false);
    }
  };

  const handleCopyDebugBundle = async () => {
    const bundle = metaDebugBundle || await fetchMetaDebugBundle();
    if (bundle) {
      const bundleJson = JSON.stringify(bundle, null, 2);
      try {
        await navigator.clipboard.writeText(bundleJson);
        toast.success('Debug Bundle copié dans le presse-papier !', { 
          duration: 3000,
          description: `Bundle ID: ${bundle.bundle_id || 'N/A'}`,
          icon: '📋'
        });
      } catch (err) {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = bundleJson;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        toast.success('Debug Bundle copié !', { duration: 3000 });
      }
    }
  };

  const handleSelectInstagramAccount = async (pageId) => {
    // P0 PRODUCTION-GRADE: Validate projectId before API call
    const normalizedProjectId = normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      toast.error(
        <div className="space-y-1">
          <div className="font-semibold">Projet non sélectionné</div>
          <div className="text-xs opacity-80">Code: PROJECT_ID_REQUIRED | Stage: CONTEXT_RESOLVE</div>
        </div>,
        { duration: 6000 }
      );
      return;
    }
    
    try {
      // P0 HARDENING: Use projectPost with automatic header injection
      const response = await projectPost('/social/instagram/select-account', { page_id: pageId }, normalizedProjectId);
      
      // Handle new response format with token validation
      if (response.data.success) {
        const igUsername = response.data.ig_username;
        const hasIg = response.data.has_instagram;
        const publishReady = response.data.publish_ready;
        const tokenValidated = response.data.page_token_validated;
        
        if (!tokenValidated) {
          toast.error(response.data.blocking_message || 'Token de page invalide. Reconnectez Instagram.', {
            duration: 7000
          });
        } else if (hasIg && igUsername) {
          toast.success(`Compte @${igUsername} sélectionné ! ${publishReady ? 'Prêt à publier.' : response.data.message || ''}`, {
            duration: 5000
          });
        } else {
          toast.warning(response.data.message || 'Page sélectionnée mais sans compte Instagram lié.', {
            duration: 7000
          });
        }
        setIgAccountModalOpen(false);
        fetchInstagramStatus();
      } else {
        // Handle validation failure
        toast.error(response.data.blocking_message || response.data.message || 'Erreur lors de la sélection', {
          duration: 7000
        });
        // Still close modal and refresh to show new state
        if (response.data.selected_page_id) {
          setIgAccountModalOpen(false);
          fetchInstagramStatus();
        }
      }
    } catch (error) {
      console.error('Select IG account error:', error);
      toast.error(formatP0Error(error) || 'Erreur lors de la sélection');
    }
  };

  const handleValidateInstagramPublish = async () => {
    // P0 PRODUCTION-GRADE: Validate projectId before API call
    const normalizedProjectId = normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      toast.error(
        <div className="space-y-1">
          <div className="font-semibold">Projet non sélectionné</div>
          <div className="text-xs opacity-80">Code: PROJECT_ID_REQUIRED | Stage: CONTEXT_RESOLVE</div>
        </div>,
        { duration: 6000 }
      );
      return;
    }
    
    setIgTesting(true);
    try {
      // P0 HARDENING: Use projectPost with automatic header injection
      const response = await projectPost('/social/instagram/validate-publish', {}, normalizedProjectId);
      
      // Use the new ok: true/false format
      if (response.data.ok) {
        toast.success(`✅ Validation réussie ! @${response.data.ig_username || 'Instagram'} est prêt à publier.`, {
          duration: 5000,
          icon: '🎉'
        });
      } else {
        // Map error_code to user-friendly message
        const errorMessages = {
          'NOT_CONFIGURED': 'Configuration Instagram non trouvée.',
          'NO_IG_BUSINESS_ACCOUNT': 'Aucun compte Instagram sélectionné.',
          'MISSING_PAGE_TOKEN': 'Token de page manquant. Reconnectez Instagram.',
          'RATE_LIMITED': response.data.message || 'Action en cours, veuillez patienter.',
          'PUBLISH_VALIDATION_FAILED': 'La validation de publication a échoué.',
          'CONTAINER_ERROR': 'Erreur lors de la création du conteneur média.'
        };
        
        const message = errorMessages[response.data.error_code] || response.data.message || 'Erreur de validation';
        toast.error(`❌ ${message}`, { duration: 8000 });
        
        if (response.data.hint) {
          toast.info(`💡 ${response.data.hint}`, { duration: 8000 });
        }
      }
      fetchInstagramStatus();
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la validation');
    } finally {
      setIgTesting(false);
    }
  };

  const getInstagramStatusBadge = () => {
    if (!igDiag) return { color: 'gray', text: 'Chargement...', icon: '⏳' };
    
    // Check for empty scopes (CASE_J)
    if (igDiag.oauth_scopes_granted !== undefined && 
        Array.isArray(igDiag.oauth_scopes_granted) && 
        igDiag.oauth_scopes_granted.length === 0) {
      return { color: 'red', text: 'Aucune permission', icon: '🚫' };
    }
    
    // Check has_publish_scope
    if (igDiag.has_publish_scope === false && igDiag.status !== 'NOT_CONFIGURED') {
      return { color: 'red', text: 'Permission publication manquante', icon: '🔐' };
    }
    
    switch (igDiag.status) {
      case 'POST_READY':
        // Only show "Prêt à publier" if has_publish_scope is true
        if (igDiag.has_publish_scope === false) {
          return { color: 'orange', text: 'Reconnexion requise', icon: '🔄' };
        }
        return { color: 'green', text: 'Prêt à publier', icon: '✅' };
      case 'CONNECTED':
        return { color: 'blue', text: 'Connecté (valider)', icon: '🔵' };
      case 'OAUTH_DONE':
        return { color: 'yellow', text: 'Sélectionner compte', icon: '📷' };
      case 'APP_CONFIGURED':
        return { color: 'blue', text: 'Connecter', icon: '🔗' };
      case 'NOT_CONFIGURED':
        return { color: 'gray', text: 'Non configuré', icon: '⚙️' };
      case 'WRONG_OAUTH_FLOW':
        return { color: 'red', text: 'Mauvais token', icon: '⚠️' };
      case 'MISSING_PERMISSIONS':
        return { color: 'red', text: 'Permissions manquantes', icon: '🔐' };
      // P0 HOTFIX: New error codes from P0 hardening
      case 'PROJECT_ID_REQUIRED':
        return { color: 'orange', text: 'Projet requis', icon: '📁' };
      case 'PROJECT_ID_MISMATCH':
        return { color: 'red', text: 'Conflit projet', icon: '⚠️' };
      case 'SCOPE_AUTHORITY_INVALID':
        return { color: 'red', text: 'Token invalide', icon: '🔐' };
      case 'IG_ACCOUNT_NOT_FOUND':
        return { color: 'orange', text: 'Compte IG non trouvé', icon: '📷' };
      case 'NEEDS_RECONNECT':
        return { color: 'orange', text: 'Reconnexion requise', icon: '🔄' };
      case 'NO_FACEBOOK_PAGE':
        return { color: 'orange', text: 'Pas de Page FB', icon: '📄' };
      case 'NO_IG_BUSINESS_ACCOUNT':
        return { color: 'orange', text: 'Pas de compte IG Business', icon: '📷' };
      case 'PUBLISH_VALIDATION_FAILED':
        return { color: 'red', text: 'Validation échouée', icon: '❌' };
      case 'TOKEN_EXPIRED':
        return { color: 'red', text: 'Token expiré', icon: '⏰' };
      default:
        if (igDiag.last_graph_error) {
          return { color: 'red', text: 'Erreur Graph API', icon: '❌' };
        }
        // P0 HOTFIX: Display actual status, not generic "Inconnu"
        return { color: 'gray', text: igDiag.status || 'Inconnu', icon: '❓' };
    }
  };

  const fetchInstagramTutorial = async () => {
    // P0 PRODUCTION-GRADE: Validate projectId before API call
    const normalizedProjectId = normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      console.warn('[fetchInstagramTutorial] No projectId - skipping');
      return;
    }
    
    try {
      // P0 HARDENING: Use projectGet with automatic header injection
      const response = await projectGet('/social/instagram/tutorial', normalizedProjectId);
      setIgTutorial(response.data.tutorial);
    } catch (error) {
      console.error('Failed to fetch Instagram tutorial:', error);
    }
  };

  // Brevo (Email) Integration Functions
  const fetchBrevoStatus = async () => {
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.get('/integrations/brevo/status');
      setBrevoStatus(response.data);
      if (response.data.is_enabled !== undefined) {
        setBrevoEnabled(response.data.is_enabled);
      }
      if (response.data.auto_send_enabled !== undefined) {
        setBrevoAutoSend(response.data.auto_send_enabled);
      }
      if (response.data.sender_email) {
        setBrevoSenderEmail(response.data.sender_email);
      }
      if (response.data.sender_name) {
        setBrevoSenderName(response.data.sender_name);
      }
    } catch (error) {
      console.error('Failed to fetch Brevo status:', error);
    } finally {
      setBrevoLoading(false);
    }
  };

  const handleSaveBrevoCredentials = async () => {
    if (!brevoApiKey && !brevoStatus?.has_credentials) {
      toast.error('Clé API Brevo requise');
      return;
    }
    if (!brevoSenderEmail) {
      toast.error('Email expéditeur requis');
      return;
    }
    if (!brevoSenderName) {
      toast.error('Nom expéditeur requis');
      return;
    }
    
    setBrevoSaving(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/integrations/brevo/credentials', {
        api_key: brevoApiKey || 'keep-existing',
        sender_email: brevoSenderEmail,
        sender_name: brevoSenderName,
        is_enabled: brevoEnabled,
        auto_send_enabled: brevoAutoSend
      });
      
      if (response.data.success) {
        toast.success('Configuration Brevo sauvegardée !');
        setBrevoApiKey('');
        fetchBrevoStatus();
      } else {
        toast.error(response.data.error || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la sauvegarde');
    } finally {
      setBrevoSaving(false);
    }
  };

  const handleTestBrevo = async () => {
    setBrevoTesting(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/integrations/brevo/test', {});
      
      if (response.data.success) {
        toast.success(response.data.message || 'Email de test envoyé !');
      } else {
        toast.error(response.data.error || 'Erreur lors du test');
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors du test');
    } finally {
      setBrevoTesting(false);
    }
  };

  const handleDeleteBrevoCredentials = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer votre intégration Brevo ?')) return;
    try {
      // P0 HARDENING: Use centralized apiClient
      await apiClient.delete('/integrations/brevo');
      toast.success('Intégration Brevo supprimée');
      setBrevoStatus(null);
      setBrevoApiKey('');
      setBrevoSenderEmail('');
      setBrevoSenderName('');
      setBrevoEnabled(true);
      setBrevoAutoSend(false);
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la suppression');
    }
  };

  const handleToggleAutomation = async (enabled) => {
    setSaving(true);
    try {
      await apiClient.put(`/projects/${projectId}/automation`, { enabled });
      setProject({ ...project, automation_enabled: enabled });
      toast.success(enabled ? 'Automatisation activée' : 'Automatisation désactivée');
    } catch (error) {
      toast.error(t('errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateChannel = async (channelId, config) => {
    setSaving(true);
    try {
      await apiClient.put(`/projects/${projectId}/channels/${channelId}`, config);
      const updatedChannels = { ...project.channels_config, [channelId]: config };
      setProject({ ...project, channels_config: updatedChannels });
      toast.success('Canal mis à jour');
    } catch (error) {
      toast.error(t('errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleRecrawl = async () => {
    setRecrawling(true);
    try {
      await apiClient.post(`/projects/${projectId}/recrawl`);
      toast.success(t('crawlInProgress'));
      // Refresh after a delay to get new themes
      setTimeout(fetchProject, 5000);
    } catch (error) {
      toast.error(t('errorSaving'));
    } finally {
      setRecrawling(false);
    }
  };

  const handleSaveThemes = async () => {
    if (!editingThemes) return;
    
    setSaving(true);
    try {
      await apiClient.put(`/projects/${projectId}/themes`, editingThemes);
      setProject({
        ...project,
        extracted_themes: { ...project.extracted_themes, ...editingThemes }
      });
      setEditingThemes(null);
      toast.success(t('savedSuccess'));
    } catch (error) {
      toast.error(t('errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleValidateThemes = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/projects/${projectId}/themes`, { validated: true });
      setProject({
        ...project,
        extracted_themes: { ...project.extracted_themes, validated: true }
      });
      toast.success(t('themesValidated'));
    } catch (error) {
      toast.error(t('errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const parseThemeList = (value) => {
    return value.split(',').map(s => s.trim()).filter(s => s.length > 0);
  };

  if (loading || !project) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('loading')}</p>
        </div>
      </div>
    );
  }

  const themes = project.extracted_themes || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      <header className="border-b bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/projects/${projectId}/calendar`)} data-testid="back-to-calendar-button">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-heading font-bold">{t('settings')}</h1>
              <p className="text-sm text-muted-foreground">{project.name}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Extracted Themes Card */}
        <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  {t('extractedThemes')}
                </CardTitle>
                <CardDescription className="mt-2">
                  Données marketing extraites automatiquement de votre site. Ces thèmes guident la génération de contenu.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRecrawl}
                  disabled={recrawling}
                  className="gap-2"
                >
                  {recrawling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {t('recrawlSite')}
                </Button>
                {!themes.validated && Object.keys(themes).length > 0 && (
                  <Button 
                    size="sm" 
                    onClick={handleValidateThemes}
                    disabled={saving}
                    className="gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {t('validateThemes')}
                  </Button>
                )}
              </div>
            </div>
            {themes.validated ? (
              <Badge className="bg-success/10 text-success border-success/20 w-fit mt-2">
                <CheckCircle className="w-3 h-3 mr-1" />
                {t('validated')}
              </Badge>
            ) : (
              <Badge variant="outline" className="w-fit mt-2 text-warning border-warning/30">
                <AlertCircle className="w-3 h-3 mr-1" />
                {t('themesNotValidated')}
              </Badge>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {Object.keys(themes).length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">{t('crawlInProgress')}</p>
              </div>
            ) : (
              <>
                {/* Theme Categories Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Destinations */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" />
                      {t('destinations')}
                    </Label>
                    {editingThemes ? (
                      <Textarea
                        value={editingThemes.destinations?.join(', ') || themes.destinations?.join(', ') || ''}
                        onChange={(e) => setEditingThemes({
                          ...editingThemes,
                          destinations: parseThemeList(e.target.value)
                        })}
                        placeholder="Paris, Lyon, Marseille..."
                        className="min-h-[60px]"
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {themes.destinations?.map((d, i) => (
                          <Badge key={i} variant="secondary">{d}</Badge>
                        )) || <span className="text-muted-foreground text-sm">-</span>}
                      </div>
                    )}
                  </div>

                  {/* Monuments */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-primary" />
                      {t('monuments')}
                    </Label>
                    {editingThemes ? (
                      <Textarea
                        value={editingThemes.monuments?.join(', ') || themes.monuments?.join(', ') || ''}
                        onChange={(e) => setEditingThemes({
                          ...editingThemes,
                          monuments: parseThemeList(e.target.value)
                        })}
                        placeholder="Tour Eiffel, Louvre..."
                        className="min-h-[60px]"
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {themes.monuments?.map((m, i) => (
                          <Badge key={i} variant="secondary">{m}</Badge>
                        )) || <span className="text-muted-foreground text-sm">-</span>}
                      </div>
                    )}
                  </div>

                  {/* Experiences */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      {t('experiences')}
                    </Label>
                    {editingThemes ? (
                      <Textarea
                        value={editingThemes.experiences?.join(', ') || themes.experiences?.join(', ') || ''}
                        onChange={(e) => setEditingThemes({
                          ...editingThemes,
                          experiences: parseThemeList(e.target.value)
                        })}
                        placeholder="Visite guidée, Dégustation..."
                        className="min-h-[60px]"
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {themes.experiences?.map((e, i) => (
                          <Badge key={i} variant="secondary">{e}</Badge>
                        )) || <span className="text-muted-foreground text-sm">-</span>}
                      </div>
                    )}
                  </div>

                  {/* Editorial Angles */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-primary" />
                      {t('editorialAngles')}
                    </Label>
                    {editingThemes ? (
                      <Textarea
                        value={editingThemes.editorial_angles?.join(', ') || themes.editorial_angles?.join(', ') || ''}
                        onChange={(e) => setEditingThemes({
                          ...editingThemes,
                          editorial_angles: parseThemeList(e.target.value)
                        })}
                        placeholder="Découverte, Conseils pratiques..."
                        className="min-h-[60px]"
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {themes.editorial_angles?.map((a, i) => (
                          <Badge key={i} variant="outline">{a}</Badge>
                        )) || <span className="text-muted-foreground text-sm">-</span>}
                      </div>
                    )}
                  </div>

                  {/* Seasonality */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-primary" />
                      {t('seasonality')}
                    </Label>
                    {editingThemes ? (
                      <Textarea
                        value={editingThemes.seasonality?.join(', ') || themes.seasonality?.join(', ') || ''}
                        onChange={(e) => setEditingThemes({
                          ...editingThemes,
                          seasonality: parseThemeList(e.target.value)
                        })}
                        placeholder="Été, Noël, Vacances scolaires..."
                        className="min-h-[60px]"
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {themes.seasonality?.map((s, i) => (
                          <Badge key={i} variant="outline">{s}</Badge>
                        )) || <span className="text-muted-foreground text-sm">-</span>}
                      </div>
                    )}
                  </div>

                  {/* Keywords */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-primary" />
                      {t('keywords')}
                    </Label>
                    {editingThemes ? (
                      <Textarea
                        value={editingThemes.keywords?.join(', ') || themes.keywords?.join(', ') || ''}
                        onChange={(e) => setEditingThemes({
                          ...editingThemes,
                          keywords: parseThemeList(e.target.value)
                        })}
                        placeholder="marketing, digital, innovation..."
                        className="min-h-[60px]"
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {themes.keywords?.map((k, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">{k}</Badge>
                        )) || <span className="text-muted-foreground text-sm">-</span>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Brand Tone & Target Audience */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-primary" />
                      {t('brandTone')}
                    </Label>
                    <p className="text-sm bg-muted/50 rounded-lg p-2">{themes.brand_tone || '-'}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-primary" />
                      {t('targetAudience')}
                    </Label>
                    <p className="text-sm bg-muted/50 rounded-lg p-2">{themes.target_audience || '-'}</p>
                  </div>
                </div>

                {/* Edit/Save buttons */}
                <div className="flex gap-2 pt-4 border-t">
                  {editingThemes ? (
                    <>
                      <Button onClick={handleSaveThemes} disabled={saving}>
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        {t('save')}
                      </Button>
                      <Button variant="outline" onClick={() => setEditingThemes(null)}>
                        {t('cancel')}
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" onClick={() => setEditingThemes({})}>
                      Modifier les thèmes
                    </Button>
                  )}
                </div>

                {/* Last crawl info */}
                {themes.last_crawl && (
                  <p className="text-xs text-muted-foreground">
                    {t('lastCrawl')}: {new Date(themes.last_crawl).toLocaleString()}
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Automation Toggle */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Zap className="w-5 h-5 text-primary" />
                  Mode Automatique
                </CardTitle>
                <CardDescription className="mt-2">
                  Activer la publication automatique des contenus validés et brouillons (les contenus désactivés ne sont jamais publiés)
                </CardDescription>
              </div>
              <Switch
                checked={project.automation_enabled}
                onCheckedChange={handleToggleAutomation}
                disabled={saving}
                data-testid="automation-toggle"
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className={`p-4 rounded-lg border ${project.automation_enabled ? 'bg-success/5 border-success/20' : 'bg-muted border-border'}`}>
              <div className="flex items-start gap-3">
                {project.automation_enabled ? (
                  <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-muted-foreground mt-0.5" />
                )}
                <div>
                  <p className="font-medium">
                    {project.automation_enabled ? 'Automatisation active' : 'Automatisation en pause'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {project.automation_enabled
                      ? 'Les contenus validés et brouillons seront publiés automatiquement selon le calendrier. Les contenus désactivés sont exclus.'
                      : 'Les contenus continuent d\'être générés mais ne seront pas publiés.'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Domain Setup Section - NEW */}
        <DomainSetup token={token} onDomainVerified={fetchCallbackUrls} />

        {/* Channels Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Canaux de publication</CardTitle>
            <CardDescription>
              Configurez les connexions API pour chaque plateforme
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Callback URLs Info Card */}
            <Card className={`${callbackUrls.custom_domain ? 'border-green-200 bg-green-50/50' : 'border-blue-200 bg-blue-50/50'}`}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${callbackUrls.custom_domain ? 'bg-green-100' : 'bg-blue-100'}`}>
                    <LinkIcon className={`w-4 h-4 ${callbackUrls.custom_domain ? 'text-green-600' : 'text-blue-600'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className={`font-medium text-sm ${callbackUrls.custom_domain ? 'text-green-800' : 'text-blue-800'}`}>
                        URLs de Callback
                      </h4>
                      {callbackUrls.custom_domain ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Domaine personnalisé
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Domaine temporaire
                        </Badge>
                      )}
                    </div>
                    <p className={`text-xs mb-2 ${callbackUrls.custom_domain ? 'text-green-600' : 'text-blue-600'}`}>
                      {callbackUrls.custom_domain 
                        ? `URLs basées sur votre domaine vérifié : ${callbackUrls.custom_domain}`
                        : 'Configurez un domaine personnalisé (ci-dessus) pour des URLs stables pour les intégrations OAuth.'
                      }
                    </p>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium w-20 ${callbackUrls.custom_domain ? 'text-green-700' : 'text-blue-700'}`}>X (Twitter):</span>
                        <code className={`flex-1 px-2 py-1 rounded break-all ${callbackUrls.custom_domain ? 'bg-white text-green-800' : 'bg-white text-blue-800'}`}>{callbackUrls.x || redirectUri}</code>
                        <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => copyToClipboard(callbackUrls.x || redirectUri)}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium w-20 ${callbackUrls.custom_domain ? 'text-green-700' : 'text-blue-700'}`}>Facebook:</span>
                        <code className={`flex-1 px-2 py-1 rounded break-all ${callbackUrls.custom_domain ? 'bg-white text-green-800' : 'bg-white text-blue-800'}`}>{callbackUrls.facebook || 'https://ai-marketing-engine.com/api/auth/facebook/callback'}</code>
                        <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => copyToClipboard(callbackUrls.facebook || 'https://ai-marketing-engine.com/api/auth/facebook/callback')}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium w-20 ${callbackUrls.custom_domain ? 'text-green-700' : 'text-blue-700'}`}>Instagram:</span>
                        <code className={`flex-1 px-2 py-1 rounded break-all ${callbackUrls.custom_domain ? 'bg-white text-green-800' : 'bg-white text-blue-800'}`}>{callbackUrls.instagram || 'https://ai-marketing-engine.com/api/auth/instagram/callback'}</code>
                        <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => copyToClipboard(callbackUrls.instagram || 'https://ai-marketing-engine.com/api/auth/instagram/callback')}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium w-20 ${callbackUrls.custom_domain ? 'text-green-700' : 'text-blue-700'}`}>LinkedIn:</span>
                        <code className={`flex-1 px-2 py-1 rounded break-all ${callbackUrls.custom_domain ? 'bg-white text-green-800' : 'bg-white text-blue-800'}`}>{callbackUrls.linkedin || 'https://ai-marketing-engine.com/api/auth/linkedin/callback'}</code>
                        <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => copyToClipboard(callbackUrls.linkedin || 'https://ai-marketing-engine.com/api/auth/linkedin/callback')}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* X (Twitter) Channel - P0 FIX: Simplified OAuth (no Client ID/Secret input) */}
            <Card className={`border-2 ${xStatus?.connected ? 'border-green-300' : 'border-gray-200'}`} data-testid="x-twitter-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                      <Twitter className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium">X (Twitter)</h3>
                        {xStatus?.connected ? (
                          <Badge className="bg-green-100 text-green-800 border-green-200" data-testid="x-connected-badge">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Connecté @{xStatus.x_username}
                          </Badge>
                        ) : (
                          <Badge variant="outline" data-testid="x-not-connected-badge">Non connecté</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">Publication automatique via OAuth 2.0 PKCE</p>
                    </div>
                  </div>
                  <Switch
                    checked={project.channels_config?.x_twitter?.enabled || xStatus?.connected || false}
                    onCheckedChange={(enabled) => handleUpdateChannel('x_twitter', { ...project.channels_config?.x_twitter, enabled })}
                    disabled={saving}
                    data-testid="x-enabled-switch"
                  />
                </div>

                {/* P0 FIX: Simple Connect/Disconnect - NO Client ID/Secret */}
                {!xStatus?.connected ? (
                  <div className="space-y-4">
                    <Button 
                      onClick={handleConnectX} 
                      disabled={xConnecting} 
                      className="w-full" 
                      size="lg"
                      data-testid="x-connect-btn"
                    >
                      {xConnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Twitter className="w-4 h-4 mr-2" />}
                      Connecter mon compte X
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                      Autorisez AI Marketing Engine à publier sur votre compte X
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Test Tweet Section */}
                    <div className="p-4 border rounded-lg bg-green-50">
                      <h4 className="font-medium flex items-center gap-2 mb-3">
                        <Send className="w-4 h-4" /> Post de test
                      </h4>
                      <div className="relative">
                        <Input
                          value={testTweetText}
                          onChange={(e) => setTestTweetText(e.target.value)}
                          placeholder="Texte du tweet..."
                          maxLength={280}
                          data-testid="x-test-tweet-input"
                          className={testTweetText.length > 280 ? 'border-red-500' : ''}
                        />
                        {/* P0 FIX: Character counter */}
                        <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${
                          testTweetText.length > 280 ? 'text-red-500 font-bold' : 
                          testTweetText.length > 250 ? 'text-orange-500' : 
                          'text-muted-foreground'
                        }`}>
                          {testTweetText.length}/280
                        </span>
                      </div>
                      {testTweetText.length > 280 && (
                        <p className="text-xs text-red-500 mt-1">
                          ⚠️ Le tweet dépasse 280 caractères
                        </p>
                      )}
                      <div className="flex gap-2 mt-3">
                        <Button 
                          type="button" 
                          onClick={handleTestXPost} 
                          disabled={xTesting || testTweetText.length > 280 || !testTweetText.trim()} 
                          className="flex-1"
                          data-testid="x-test-post-btn"
                        >
                          {xTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                          Post de test
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={handleDisconnectX}
                          data-testid="x-disconnect-btn"
                        >
                          Déconnecter
                        </Button>
                      </div>
                    </div>
                    
                    {/* Connection Info */}
                    <div className="p-3 border rounded-lg bg-muted/30 text-sm">
                      <div className="flex justify-between mb-1">
                        <span className="text-muted-foreground">Compte:</span>
                        <span className="font-medium">@{xStatus.x_username}</span>
                      </div>
                      {xStatus.connected_at && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Connecté le:</span>
                          <span className="text-xs">{new Date(xStatus.connected_at).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Telegram Channel - Bot Integration */}
            <Card className={`border-2 ${tgStatus?.is_enabled ? 'border-green-300' : tgStatus?.has_credentials ? 'border-yellow-300' : 'border-gray-200'}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 rounded bg-blue-500">
                        <MessageCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-medium">Telegram</h3>
                        {tgStatus?.is_enabled ? (
                          <Badge className="bg-green-100 text-green-800 text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Activé {tgStatus.bot_username && `@${tgStatus.bot_username}`}
                          </Badge>
                        ) : tgStatus?.has_credentials ? (
                          <Badge variant="outline" className="text-yellow-600 text-xs">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Configuré (désactivé)
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500 text-xs">Non configuré</Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground ml-9">Messages automatiques vers un canal Telegram</p>
                  </div>
                  <Switch 
                    checked={tgEnabled}
                    onCheckedChange={setTgEnabled}
                    disabled={!tgStatus?.has_credentials}
                  />
                </div>

                {tgLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Tutorial Accordion */}
                    <Accordion type="single" collapsible className="border rounded-lg">
                      <AccordionItem value="tutorial" className="border-none">
                        <AccordionTrigger className="px-4 py-3 hover:no-underline">
                          <div className="flex items-center gap-2 text-sm">
                            <HelpCircle className="w-4 h-4 text-blue-500" />
                            <span>Comment connecter Telegram (5 minutes)</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4 space-y-4 text-sm">
                          <div>
                            <h4 className="font-semibold text-blue-600 mb-2">Étape 1 — Créer un bot</h4>
                            <ol className="list-decimal ml-4 space-y-1 text-muted-foreground">
                              <li>Ouvrir Telegram et rechercher <span className="font-mono bg-muted px-1 rounded">@BotFather</span></li>
                              <li>Envoyer <span className="font-mono bg-muted px-1 rounded">/newbot</span></li>
                              <li>Choisir un nom et un username pour votre bot</li>
                              <li>Copier le <strong>Bot Token</strong> (ex: <span className="font-mono text-xs">123456:ABC-DEF...</span>)</li>
                            </ol>
                          </div>
                          <div>
                            <h4 className="font-semibold text-blue-600 mb-2">Étape 2 — Créer un canal</h4>
                            <ol className="list-decimal ml-4 space-y-1 text-muted-foreground">
                              <li>Telegram → Nouveau → Nouveau canal</li>
                              <li>Nommer le canal (ex: "Travel Trip & Tips")</li>
                              <li>Dans le canal → Administrateurs → Ajouter un admin</li>
                              <li>Ajouter le bot et lui donner le droit de <strong>poster des messages</strong></li>
                            </ol>
                          </div>
                          <div>
                            <h4 className="font-semibold text-blue-600 mb-2">Étape 3 — Obtenir le Chat ID</h4>
                            <p className="text-muted-foreground mb-2"><strong>Option A (recommandée)</strong> : Utilisez le handle du canal public</p>
                            <p className="ml-4 font-mono bg-muted px-2 py-1 rounded inline-block text-xs">@VotreCanal</p>
                            <p className="text-muted-foreground mt-2"><strong>Option B</strong> : Cliquez sur "Détecter Chat ID" après avoir configuré le bot token</p>
                          </div>
                          <div>
                            <h4 className="font-semibold text-blue-600 mb-2">Étape 4 — Configurer</h4>
                            <ol className="list-decimal ml-4 space-y-1 text-muted-foreground">
                              <li>Coller le Bot Token ci-dessous</li>
                              <li>Coller le Chat ID / handle du canal</li>
                              <li>Cliquer "Tester la connexion"</li>
                            </ol>
                          </div>
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                            <h4 className="font-semibold text-yellow-800 mb-1">🔧 Dépannage</h4>
                            <ul className="text-xs text-yellow-700 space-y-1">
                              <li><strong>"chat not found"</strong> : Bot pas admin ou mauvais chat ID</li>
                              <li><strong>"not enough rights"</strong> : Donner le droit de poster au bot</li>
                              <li><strong>Aucun message</strong> : Vérifier le token</li>
                            </ul>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>

                    {/* Configuration Form */}
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Bot Token {!tgStatus?.has_credentials && <span className="text-red-500">*</span>}</Label>
                        <div className="relative">
                          <Input
                            type={showTgToken ? "text" : "password"}
                            value={tgBotToken}
                            onChange={(e) => setTgBotToken(e.target.value)}
                            placeholder={tgStatus?.has_credentials ? "••••••••••• (laisser vide pour conserver)" : "123456:ABC-DEF..."}
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowTgToken(!showTgToken)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showTgToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {tgStatus?.has_credentials && (
                          <p className="text-xs text-muted-foreground mt-1">Token actuel : {tgStatus.bot_token_masked}</p>
                        )}
                      </div>

                      <div>
                        <Label className="text-xs text-muted-foreground">Chat ID / Canal <span className="text-red-500">*</span></Label>
                        <div className="flex gap-2">
                          <Input
                            value={tgChatId}
                            onChange={(e) => setTgChatId(e.target.value)}
                            placeholder="@MonCanal ou -1001234567890"
                            className="flex-1"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleFetchTelegramChats}
                            disabled={tgFetchingChats || !tgStatus?.has_credentials}
                            title="Détecter les chats disponibles"
                          >
                            {tgFetchingChats ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                          </Button>
                        </div>
                        {tgAvailableChats.length > 0 && (
                          <div className="mt-2 space-y-1">
                            <p className="text-xs text-muted-foreground">Chats détectés :</p>
                            {tgAvailableChats.map((chat) => (
                              <button
                                key={chat.id}
                                onClick={() => setTgChatId(chat.id)}
                                className="block w-full text-left px-2 py-1 text-xs bg-muted rounded hover:bg-muted/80"
                              >
                                <span className="font-medium">{chat.title}</span>
                                <span className="text-muted-foreground ml-2">({chat.type}: {chat.id})</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-muted-foreground">Parse Mode</Label>
                          <Select value={tgParseMode} onValueChange={setTgParseMode}>
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="HTML">HTML</SelectItem>
                              <SelectItem value="MarkdownV2">MarkdownV2</SelectItem>
                              <SelectItem value="None">None</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2 pt-5">
                          <Switch
                            id="tg-preview"
                            checked={tgDisablePreview}
                            onCheckedChange={setTgDisablePreview}
                          />
                          <Label htmlFor="tg-preview" className="text-xs">Désactiver aperçu liens</Label>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button
                          onClick={handleSaveTelegramCredentials}
                          disabled={tgSaving || (!tgBotToken && !tgStatus?.has_credentials) || !tgChatId}
                          className="flex-1"
                        >
                          {tgSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
                          Sauvegarder
                        </Button>
                        {tgStatus?.has_credentials && (
                          <Button variant="destructive" size="icon" onClick={handleDeleteTelegramCredentials}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>

                      {tgStatus?.has_credentials && (
                        <div className="flex gap-2 pt-2">
                          <Button
                            variant="outline"
                            onClick={handleTestTelegram}
                            disabled={tgTesting}
                            className="flex-1"
                          >
                            {tgTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                            Tester connexion
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleSendSampleTelegram}
                            disabled={tgTesting}
                            className="flex-1"
                          >
                            {tgTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                            Post exemple
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Facebook Channel - Connection Status Panel */}
            <Card className={`border-2 ${
              fbDiag?.status === 'READY' ? 'border-green-300' : 
              fbDiag?.status === 'NEEDS_PAGE_SELECTION' ? 'border-yellow-300' : 
              fbStatus?.has_credentials ? 'border-orange-300' : 'border-gray-200'
            }`}>
              <CardContent className="pt-6">
                {/* Header with Status Badge */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 rounded bg-blue-600">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      </div>
                      <div>
                        <h3 className="font-medium">Facebook Pages</h3>
                        {/* Dynamic Status Badge */}
                        {fbDiag && (
                          <Badge className={`text-xs ${
                            getFbStatusBadge().color === 'green' ? 'bg-green-100 text-green-800' :
                            getFbStatusBadge().color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                            getFbStatusBadge().color === 'orange' ? 'bg-orange-100 text-orange-800' :
                            getFbStatusBadge().color === 'red' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            <span className="mr-1">{getFbStatusBadge().icon}</span>
                            {getFbStatusBadge().text}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground ml-9">Publication automatique sur vos Pages Facebook</p>
                    {fbPollingActive && (
                      <p className="text-xs text-blue-500 ml-9 animate-pulse">🔄 Mise à jour en cours...</p>
                    )}
                  </div>
                  <Switch 
                    checked={fbEnabled}
                    onCheckedChange={setFbEnabled}
                    disabled={fbDiag?.status !== 'READY'}
                  />
                </div>

                {fbLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    
                    {/* CONNECTION STATUS PANEL */}
                    {fbDiag && (
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3 border">
                        <h4 className="font-medium text-sm flex items-center gap-2">
                          📊 État de connexion
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => { fetchFacebookDiag(); fetchFacebookStatus(); }}
                            className="h-6 px-2"
                          >
                            <RefreshCw className="w-3 h-3" />
                          </Button>
                        </h4>
                        
                        {/* STATUS BADGES ROW */}
                        <div className="flex flex-wrap gap-2">
                          {/* OAuth Status */}
                          <Badge className={`text-xs ${
                            fbDiag.oauth_status === 'DONE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                          }`}>
                            OAuth: {fbDiag.oauth_status === 'DONE' ? '✓ OK' : '✗ NON'}
                          </Badge>
                          
                          {/* User Token Status */}
                          <Badge className={`text-xs ${
                            fbDiag.user_token_status === 'OK' ? 'bg-green-100 text-green-800' :
                            fbDiag.user_token_status === 'EXPIRED' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            User Token: {
                              fbDiag.user_token_status === 'OK' ? '✓ OK' :
                              fbDiag.user_token_status === 'EXPIRED' ? '⏰ EXPIRÉ' :
                              '✗ ABSENT'
                            }
                          </Badge>
                          
                          {/* Pages Count */}
                          <Badge className={`text-xs ${
                            fbDiag.pages_count > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                          }`}>
                            Pages: {fbDiag.pages_count || 0}
                          </Badge>
                          
                          {/* Page Token Status */}
                          <Badge className={`text-xs ${
                            fbDiag.page_token_status === 'OK' ? 'bg-green-100 text-green-800' :
                            fbDiag.page_token_status === 'EXPIRED' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            Page Token: {
                              fbDiag.page_token_status === 'OK' ? '✓ OK' :
                              fbDiag.page_token_status === 'EXPIRED' ? '⏰ EXPIRÉ' :
                              '✗ ABSENT'
                            }
                          </Badge>
                          
                          {/* Can Publish */}
                          <Badge className={`text-xs ${
                            fbDiag.can_publish ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                          }`}>
                            Publier: {fbDiag.can_publish ? '✓ OUI' : '✗ NON'}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Page sélectionnée</span>
                            <span className="font-medium">
                              {fbDiag.selected_page_name ? (
                                <>{fbDiag.selected_page_name} <span className="text-gray-400">({fbDiag.selected_page_id?.slice(0,8)}...)</span></>
                              ) : (
                                <span className="text-orange-600">Non sélectionnée</span>
                              )}
                            </span>
                          </div>
                          
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Token page</span>
                            <span className={fbDiag.has_page_token ? 'text-green-600' : 'text-red-600'}>
                              {fbDiag.has_page_token ? '✓ Oui' : '✗ Non'}
                            </span>
                          </div>
                          
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Expiration token</span>
                            <span className={fbDiag.days_until_expiry && fbDiag.days_until_expiry < 7 ? 'text-orange-600' : ''}>
                              {fbDiag.token_expires_at ? formatFbDate(fbDiag.token_expires_at) : 'N/A'}
                              {fbDiag.days_until_expiry !== undefined && ` (${fbDiag.days_until_expiry}j)`}
                            </span>
                          </div>
                          
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Pages disponibles</span>
                            <span>{fbDiag.pages_count || 0}</span>
                          </div>
                        </div>
                        
                        {/* Last Graph Error */}
                        {fbDiag.last_graph_error && (
                          <div className="bg-red-50 border border-red-200 rounded p-2 text-xs">
                            <p className="font-medium text-red-700">⚠️ Dernière erreur Graph API:</p>
                            <p className="text-red-600">{fbDiag.last_graph_error.message || JSON.stringify(fbDiag.last_graph_error)}</p>
                            {fbDiag.last_graph_error.code && <p className="text-red-500">Code: {fbDiag.last_graph_error.code}</p>}
                            {fbDiag.last_graph_error.fbtrace_id && <p className="text-red-400 text-[10px]">fbtrace: {fbDiag.last_graph_error.fbtrace_id}</p>}
                          </div>
                        )}
                        
                        {/* Next Action */}
                        {fbDiag.next_action && fbDiag.status !== 'READY' && (
                          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
                            <p className="text-blue-800">
                              <strong>Action requise:</strong> {
                                fbDiag.next_action === 'SELECT_PAGE' ? 'Sélectionnez une Page Facebook' :
                                fbDiag.next_action === 'RUN_OAUTH' ? 'Cliquez sur "Connecter Facebook"' :
                                fbDiag.next_action === 'RECONNECT' ? 'Déconnectez et reconnectez Facebook' :
                                fbDiag.next_action === 'ADD_ROLE_TO_APP' ? 'Ajoutez l&apos;app aux rôles de votre Page Facebook' :
                                fbDiag.next_action
                              }
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* LAST TEST RESULT */}
                    {fbLastTest && (
                      <div className={`rounded-lg p-3 text-xs border ${
                        fbLastTest.status === 'SUCCESS' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                      }`}>
                        <p className="font-medium mb-1">
                          {fbLastTest.status === 'SUCCESS' ? '✅ Dernier test réussi' : '❌ Dernier test échoué'}
                        </p>
                        <p className="text-muted-foreground">{formatFbDate(fbLastTest.at || fbLastTest.published_at)}</p>
                        {fbLastTest.status === 'SUCCESS' && fbLastTest.post_url && (
                          <a 
                            href={fbLastTest.post_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline flex items-center gap-1 mt-1"
                          >
                            <ExternalLink className="w-3 h-3" /> Ouvrir le post
                          </a>
                        )}
                        {fbLastTest.status === 'FAIL' && fbLastTest.error && (
                          <p className="text-red-600 mt-1">Erreur: {fbLastTest.error}</p>
                        )}
                      </div>
                    )}

                    {/* APP CONFIG SECTION - Multi-tenant */}
                    <div className="space-y-4 border rounded-lg p-4 bg-gray-50/50">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium">Utiliser ma propre app Meta</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {fbUseCustomApp 
                              ? 'Vous utilisez votre propre application Facebook' 
                              : fbHasDefaultApp 
                                ? 'Utilise l\'app par défaut (recommandé)'
                                : 'Aucune app par défaut disponible - configurez la vôtre'}
                          </p>
                        </div>
                        <Switch 
                          checked={fbUseCustomApp}
                          onCheckedChange={(checked) => {
                            setFbUseCustomApp(checked);
                            if (!checked) {
                              setFbAppId('');
                              setFbAppSecret('');
                            }
                          }}
                          data-testid="fb-use-custom-app-toggle"
                        />
                      </div>
                      
                      {/* App Config Source Badge */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">Source:</span>
                        <Badge className={`text-xs ${
                          fbDiag?.app_config_source === 'CUSTOM' ? 'bg-purple-100 text-purple-800' :
                          fbDiag?.app_config_source === 'DEFAULT' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {fbDiag?.app_config_source === 'CUSTOM' ? '🔑 App personnalisée' :
                           fbDiag?.app_config_source === 'DEFAULT' ? '✓ App par défaut' :
                           '⚠️ Non configurée'}
                        </Badge>
                        {fbDiag?.has_custom_app_id && (
                          <Badge variant="outline" className="text-xs">ID: ✓</Badge>
                        )}
                        {fbDiag?.has_custom_app_secret && (
                          <Badge variant="outline" className="text-xs">Secret: ✓</Badge>
                        )}
                      </div>
                      
                      {/* Custom App Fields - Only show when toggle is ON */}
                      {fbUseCustomApp && (
                        <div className="space-y-3 pt-2 border-t">
                          <div className="space-y-2">
                            <Label htmlFor="fb-app-id" className="text-xs">
                              Facebook App ID <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id="fb-app-id"
                              placeholder="Ex: 123456789012345"
                              value={fbAppId}
                              onChange={(e) => setFbAppId(e.target.value)}
                              className="font-mono text-sm"
                              data-testid="fb-app-id-input"
                            />
                            {fbDiag?.has_custom_app_id && !fbAppId && (
                              <p className="text-xs text-green-600">✓ App ID déjà configuré</p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="fb-app-secret" className="text-xs">
                              Facebook App Secret {!fbDiag?.has_custom_app_secret && <span className="text-red-500">*</span>}
                            </Label>
                            <div className="relative">
                              <Input
                                id="fb-app-secret"
                                type={showFbSecret ? 'text' : 'password'}
                                placeholder={fbDiag?.has_custom_app_secret ? '••••••••••• (laisser vide pour conserver)' : 'Ex: abc123def456...'}
                                value={fbAppSecret}
                                onChange={(e) => setFbAppSecret(e.target.value)}
                                className="font-mono text-sm pr-10"
                                data-testid="fb-app-secret-input"
                              />
                              <button
                                type="button"
                                onClick={() => setShowFbSecret(!showFbSecret)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                {showFbSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                            {fbDiag?.has_custom_app_secret && (
                              <p className="text-xs text-green-600">✓ App Secret déjà configuré</p>
                            )}
                          </div>
                          
                          <Button 
                            onClick={handleSaveFacebookConfig} 
                            disabled={fbSaving || !fbAppId || (!fbAppSecret && !fbDiag?.has_custom_app_secret)} 
                            className="w-full"
                            data-testid="fb-save-config-btn"
                          >
                            {fbSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                            Sauvegarder la configuration
                          </Button>
                        </div>
                      )}
                      
                      {/* Default app info when toggle is OFF */}
                      {!fbUseCustomApp && (
                        <div className="text-xs">
                          {fbHasDefaultApp ? (
                            <p className="text-green-600">✓ Une app Meta par défaut est disponible. Vous pouvez connecter directement.</p>
                          ) : (
                            <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                              <p className="text-yellow-800 font-medium">⚠️ Aucune app par défaut</p>
                              <p className="text-yellow-700 mt-1">Activez "Utiliser ma propre app" et entrez vos credentials Facebook.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ACTION BUTTONS - Based on status */}
                    <div className="space-y-3">
                      {/* Connect button - show when app is configured but not connected */}
                      {(fbDiag?.app_config_source !== 'NONE') && 
                       (!fbDiag?.has_user_token && !fbDiag?.has_page_token && fbDiag?.status !== 'NEEDS_PAGE_SELECTION') && (
                        <Button 
                          onClick={handleConnectFacebook} 
                          disabled={fbConnecting || (fbUseCustomApp && !fbDiag?.has_custom_app_id)}
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          data-testid="fb-connect-btn"
                        >
                          {fbConnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                          Connecter Facebook
                        </Button>
                      )}

                      {/* If NEEDS_PAGE_SELECTION - show Page Selector button */}
                      {fbDiag?.status === 'NEEDS_PAGE_SELECTION' && (
                        <Button onClick={handleOpenPageSelector} disabled={fbLoadingPages} className="w-full bg-yellow-500 hover:bg-yellow-600">
                          {fbLoadingPages ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                          Choisir une Page
                        </Button>
                      )}

                      {/* If READY - show Test, Refresh, Disconnect */}
                      {fbDiag?.status === 'READY' && (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <Button onClick={handleTestFacebookPost} disabled={fbTesting} className="flex-1">
                              {fbTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                              Tester publication
                            </Button>
                            <Button variant="outline" onClick={handleOpenPageSelector} className="flex-1">
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Changer de Page
                            </Button>
                          </div>
                          <Button 
                            variant="outline" 
                            onClick={handleDisconnectFacebook}
                            className="w-full text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Déconnecter Facebook
                          </Button>
                        </div>
                      )}
                      
                      {/* Delete credentials button */}
                      {fbStatus?.has_credentials && (
                        <Button variant="ghost" size="sm" onClick={handleDeleteFacebookCredentials} className="text-xs text-muted-foreground hover:text-red-600">
                          Supprimer toute la configuration
                        </Button>
                      )}
                    </div>

                    {/* APP REVIEW INFO - Collapsible */}
                    <details className="text-xs bg-blue-50 rounded-lg border border-blue-200">
                      <summary className="cursor-pointer p-2 font-medium text-blue-800">
                        ℹ️ Pourquoi ces permissions ? (App Review)
                      </summary>
                      <div className="p-2 pt-0 text-blue-700 space-y-1">
                        <p>Ces permissions sont le <strong>minimum requis</strong> pour publier sur une Page Facebook :</p>
                        <ul className="list-disc ml-4">
                          <li><strong>pages_show_list</strong> : Lister vos Pages gérées</li>
                          <li><strong>pages_manage_posts</strong> : Créer des publications</li>
                          <li><strong>pages_read_engagement</strong> : Requis par Meta pour valider l'accès</li>
                        </ul>
                        <p className="text-blue-600 mt-2">Utilisez le bouton "Tester publication" pour démontrer le bon fonctionnement.</p>
                      </div>
                    </details>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* PAGE SELECTION MODAL */}
            {fbPageModalOpen && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-lg">Sélectionner une Page Facebook</h3>
                    <button onClick={() => setFbPageModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {fbLoadingPages ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    </div>
                  ) : fbPages.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <p className="mb-2">Aucune page trouvée.</p>
                      <p className="text-xs">Vérifiez que vous êtes administrateur d'au moins une Page Facebook.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {fbPages.map((page) => (
                        <button
                          key={page.id}
                          onClick={() => handleSelectFacebookPage(page.id)}
                          className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{page.name}</p>
                              <p className="text-xs text-muted-foreground">ID: {page.id}</p>
                              {page.category && <p className="text-xs text-gray-500">{page.category}</p>}
                            </div>
                            <div className="text-right">
                              {page.has_access_token && (
                                <Badge className="bg-green-100 text-green-800 text-xs">✓ Token</Badge>
                              )}
                            </div>
                          </div>
                          {page.tasks && page.tasks.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {page.tasks.slice(0, 3).map((task) => (
                                <span key={task} className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded">{task}</span>
                              ))}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-4 flex gap-2">
                    <Button variant="outline" onClick={() => setFbPageModalOpen(false)} className="flex-1">
                      Annuler
                    </Button>
                    <Button variant="outline" onClick={handleFetchFacebookPages} disabled={fbLoadingPages}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${fbLoadingPages ? 'animate-spin' : ''}`} />
                      Rafraîchir
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ===== INSTAGRAM FEED POSTS INTEGRATION ===== */}
            <Card className={`border-2 ${
              igDiag?.can_publish ? 'border-pink-400' :
              igDiag?.status === 'OAUTH_DONE' ? 'border-yellow-300' :
              igDiag?.has_custom_app_id ? 'border-blue-300' :
              'border-gray-200'
            }`} data-testid="instagram-integration-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 rounded bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                      </div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">Instagram – Feed Posts</h3>
                        <button 
                          onClick={() => setIgInfoOpen(true)}
                          className="p-1 rounded-full hover:bg-muted/80 transition-colors"
                          title="Plus d'informations"
                        >
                          <AlertCircle className="w-4 h-4 text-pink-500" />
                        </button>
                      </div>
                      {/* Status Badge */}
                      {igDiag && (
                        <Badge className={`text-xs ${
                          getInstagramStatusBadge().color === 'green' ? 'bg-green-100 text-green-800' :
                          getInstagramStatusBadge().color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                          getInstagramStatusBadge().color === 'blue' ? 'bg-blue-100 text-blue-800' :
                          getInstagramStatusBadge().color === 'red' ? 'bg-red-100 text-red-800' :
                          getInstagramStatusBadge().color === 'orange' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {getInstagramStatusBadge().icon} {getInstagramStatusBadge().text}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Publiez automatiquement des images sur votre compte Instagram Business/Creator
                    </p>
                  </div>
                  <Switch 
                    checked={igEnabled} 
                    onCheckedChange={setIgEnabled} 
                    disabled={!(igUnifiedStatus?.publish_ready || igDiag?.can_publish)}
                    data-testid="ig-enabled-toggle"
                  />
                </div>

                <div className="space-y-4">
                  {/* CONNECTION STATUS PANEL */}
                  {igDiag && (
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        {/* OAuth Status */}
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            igDiag.oauth_status === 'DONE' ? 'bg-green-500' : 'bg-gray-300'
                          }`}></span>
                          <span className="text-xs">OAuth:</span>
                          <Badge variant="outline" className="text-xs">
                            {igDiag.oauth_status === 'DONE' ? '✓ OK' : '○'}
                          </Badge>
                        </div>
                        
                        {/* User Token */}
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            igDiag.user_token_status === 'OK' ? 'bg-green-500' : 'bg-gray-300'
                          }`}></span>
                          <span className="text-xs">User Token:</span>
                          <Badge variant="outline" className="text-xs">
                            {igDiag.user_token_status === 'OK' ? '✓ OK' : '○'}
                          </Badge>
                        </div>
                        
                        {/* IG Accounts Count */}
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            igDiag.ig_accounts_count > 0 ? 'bg-green-500' : 'bg-gray-300'
                          }`}></span>
                          <span className="text-xs">Comptes IG:</span>
                          <Badge variant="outline" className="text-xs">
                            {igDiag.ig_accounts_count || 0}
                          </Badge>
                        </div>
                        
                        {/* Page Token */}
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            igDiag.page_token_status === 'OK' ? 'bg-green-500' : 'bg-gray-300'
                          }`}></span>
                          <span className="text-xs">Page Token:</span>
                          <Badge variant="outline" className="text-xs">
                            {igDiag.page_token_status === 'OK' ? '✓ OK' : '○'}
                          </Badge>
                        </div>
                        
                        {/* Can Publish */}
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            igDiag.can_publish ? 'bg-green-500' : 'bg-gray-300'
                          }`}></span>
                          <span className="text-xs">Publier:</span>
                          <Badge variant="outline" className="text-xs">
                            {igDiag.can_publish ? '✓ OUI' : '○ NON'}
                          </Badge>
                        </div>
                        
                        {/* Selected Account */}
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            igDiag.ig_username ? 'bg-pink-500' : 'bg-gray-300'
                          }`}></span>
                          <span className="text-xs">Compte:</span>
                          <Badge variant="outline" className="text-xs">
                            {igDiag.ig_username ? `@${igDiag.ig_username}` : 'Non sélectionné'}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Additional Info */}
                      <div className="pt-2 border-t space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Page liée</span>
                          <span>{igUnifiedStatus?.selected_page_name || igDiag?.selected_page_name || 'Aucune'}</span>
                        </div>
                        {(igUnifiedStatus?.pages_count > 0 || igDiag?.pages_count > 0) && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Pages découvertes</span>
                            <span>{igUnifiedStatus?.pages_count || igDiag?.pages_count || 0}</span>
                          </div>
                        )}
                        {igDiag?.token_expires_at && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Token expire</span>
                            <span>{new Date(igDiag.token_expires_at).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Last Graph Error */}
                      {igDiag.last_graph_error && (
                        <div className="bg-red-50 border border-red-200 rounded p-2 text-xs">
                          <p className="font-medium text-red-700">⚠️ Dernière erreur Graph API:</p>
                          <p className="text-red-600">{igDiag.last_graph_error.message || JSON.stringify(igDiag.last_graph_error)}</p>
                          {igDiag.last_graph_error.code && <p className="text-red-500">Code: {igDiag.last_graph_error.code}</p>}
                        </div>
                      )}
                      
                      {/* Next Action */}
                      {igDiag.next_action && igDiag.status !== 'READY' && (
                        <div className="bg-pink-50 border border-pink-200 rounded p-2 text-xs">
                          <p className="text-pink-800">
                            <strong>Action requise:</strong> {igDiag.message || igDiag.next_action}
                          </p>
                        </div>
                      )}
                      
                      {/* Unified Status - Blocking Reason (IMPROVED) */}
                      {igUnifiedStatus && !igUnifiedStatus.publish_ready && igUnifiedStatus.blocking_reason && igUnifiedStatus.blocking_reason !== 'POST_READY' && igUnifiedStatus.blocking_reason !== 'POST_VALIDATED' && (
                        <div className={`border rounded p-2 text-xs mt-2 ${
                          igUnifiedStatus.blocking_reason === 'SELECT_PAGE_REQUIRED' ? 'bg-yellow-50 border-yellow-300' :
                          igUnifiedStatus.blocking_reason === 'NO_IG_BUSINESS_ACCOUNT' ? 'bg-orange-50 border-orange-300' :
                          igUnifiedStatus.blocking_reason === 'MISSING_PERMISSIONS' || igUnifiedStatus.blocking_reason === 'PAGE_TOKEN_INVALID' ? 'bg-red-50 border-red-300' :
                          igUnifiedStatus.blocking_reason === 'DISCOVERY_REQUIRED' ? 'bg-blue-50 border-blue-300' :
                          'bg-amber-50 border-amber-300'
                        }`}>
                          <p className={`font-medium ${
                            igUnifiedStatus.blocking_reason === 'SELECT_PAGE_REQUIRED' ? 'text-yellow-800' :
                            igUnifiedStatus.blocking_reason === 'NO_IG_BUSINESS_ACCOUNT' ? 'text-orange-800' :
                            igUnifiedStatus.blocking_reason === 'MISSING_PERMISSIONS' || igUnifiedStatus.blocking_reason === 'PAGE_TOKEN_INVALID' ? 'text-red-800' :
                            igUnifiedStatus.blocking_reason === 'DISCOVERY_REQUIRED' ? 'text-blue-800' :
                            'text-amber-800'
                          }`}>
                            ⚠️ {igUnifiedStatus.blocking_message}
                          </p>
                          
                          {/* CTA BUTTON ALIGNED ON blocking_action */}
                          {igUnifiedStatus.blocking_action && (
                            <div className="mt-2">
                              {igUnifiedStatus.blocking_action === 'CONNECT_OAUTH' && (
                                <Button 
                                  type="button"
                                  size="sm" 
                                  onClick={handleInstagramConnect} 
                                  disabled={igConnecting}
                                  className="w-full bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600"
                                >
                                  {igConnecting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ExternalLink className="w-3 h-3 mr-1" />}
                                  Connecter Instagram
                                </Button>
                              )}
                              {igUnifiedStatus.blocking_action === 'SELECT_PAGE' && (
                                <Button 
                                  type="button"
                                  size="sm" 
                                  onClick={() => { fetchInstagramAccounts(); setIgAccountModalOpen(true); }}
                                  disabled={igLoadingAccounts}
                                  className="w-full bg-yellow-500 hover:bg-yellow-600"
                                >
                                  {igLoadingAccounts ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                                  Sélectionner une Page
                                </Button>
                              )}
                              {igUnifiedStatus.blocking_action === 'REFRESH_ACCOUNTS' && (
                                <div className="space-y-2">
                                  <Button 
                                    size="sm" 
                                    onClick={() => handleDiscoveryRetry(false, null)}
                                    disabled={igDiscoveryRetrying || igDiscoveryLocked || igDiscoveryRetryAfter > 0}
                                    className="w-full bg-blue-500 hover:bg-blue-600 disabled:opacity-50"
                                    data-testid="discovery-retry-btn"
                                  >
                                    {igDiscoveryRetrying ? (
                                      <>
                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                        Découverte en cours...
                                      </>
                                    ) : igDiscoveryLocked ? (
                                      <>
                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                        Verrouillé...
                                      </>
                                    ) : igDiscoveryRetryAfter > 0 ? (
                                      <>
                                        ⏳ Réessayer dans {igDiscoveryRetryAfter}s
                                      </>
                                    ) : (
                                      <>
                                        <RefreshCw className="w-3 h-3 mr-1" />
                                        Réessayer la découverte
                                      </>
                                    )}
                                  </Button>
                                  
                                  {/* Force retry button - bypasses rate limit */}
                                  {igDiscoveryRetryAfter > 0 && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={handleForceDiscoveryRetry}
                                      disabled={igDiscoveryRetrying || igDiscoveryLocked}
                                      className="w-full text-xs border-gray-400"
                                      data-testid="discovery-force-retry-btn"
                                    >
                                      {igDiscoveryRetrying ? (
                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                      ) : (
                                        <RefreshCw className="w-3 h-3 mr-1" />
                                      )}
                                      J'ai corrigé, forcer retry
                                    </Button>
                                  )}
                                  
                                  {/* Observability info */}
                                  <div className="text-xs text-gray-500 space-y-1">
                                    {igDiscoveryRetryAfter > 0 && (
                                      <p className="text-center">
                                        Rate limit actif - backoff exponentiel
                                      </p>
                                    )}
                                    {igLastAttemptId && (
                                      <p className="text-center font-mono opacity-75">
                                        ID: {igLastAttemptId}
                                      </p>
                                    )}
                                    {igLastErrorSource && igLastErrorSource !== 'rate_limited' && (
                                      <p className="text-center">
                                        Cause: {igLastErrorSource === 'meta_response' ? 'Erreur Meta API' : 
                                               igLastErrorSource === 'network_error' ? 'Erreur réseau' :
                                               igLastErrorSource === 'blocked_by_waf' ? 'Bloqué WAF/Proxy' :
                                               igLastErrorSource === 'timeout' ? 'Timeout' : igLastErrorSource}
                                      </p>
                                    )}
                                    
                                    {/* Support Bundle Button */}
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={handleCopySupportBundle}
                                      className="w-full text-xs text-gray-400 hover:text-gray-600 mt-2"
                                      data-testid="copy-support-bundle-btn"
                                    >
                                      <Copy className="w-3 h-3 mr-1" />
                                      Copier bundle support
                                    </Button>
                                  </div>
                                </div>
                              )}
                              {igUnifiedStatus.blocking_action === 'REAUTHORIZE' && (
                                <Button 
                                  type="button"
                                  size="sm" 
                                  onClick={handleInstagramConnect}
                                  disabled={igConnecting}
                                  className="w-full bg-red-500 hover:bg-red-600"
                                >
                                  {igConnecting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                                  Reconnecter Instagram
                                </Button>
                              )}
                              
                              {/* P0 DEFINITIVE: AME Auth Required - Show login prompt, NOT "no_scopes" */}
                              {igUnifiedStatus.ui_state === 'ame_auth_required' && (
                                <div className="space-y-2 bg-yellow-50 border border-yellow-200 rounded p-3">
                                  <div className="flex items-center gap-2 text-yellow-800">
                                    <AlertCircle className="w-4 h-4" />
                                    <span className="font-semibold text-sm">Session AME expirée</span>
                                  </div>
                                  <p className="text-xs text-yellow-700">
                                    {igUnifiedStatus.error_message || 'Reconnectez-vous à votre compte AME pour continuer.'}
                                  </p>
                                  <Button 
                                    type="button"
                                    size="sm" 
                                    onClick={() => navigate('/login')}
                                    className="w-full bg-yellow-600 hover:bg-yellow-700 text-white"
                                  >
                                    Se reconnecter à AME
                                  </Button>
                                </div>
                              )}
                              
                              {/* P0 DEFINITIVE: Meta Unavailable - Show retry, NOT "no_scopes" */}
                              {igUnifiedStatus.ui_state === 'meta_unavailable' && (
                                <div className="space-y-2 bg-blue-50 border border-blue-200 rounded p-3">
                                  <div className="flex items-center gap-2 text-blue-800">
                                    <AlertCircle className="w-4 h-4" />
                                    <span className="font-semibold text-sm">Meta API temporairement indisponible</span>
                                  </div>
                                  <p className="text-xs text-blue-700">
                                    {igUnifiedStatus.error_message || 'L\'API Meta est momentanément inaccessible. Réessayez dans quelques instants.'}
                                  </p>
                                  <Button 
                                    type="button"
                                    size="sm" 
                                    onClick={fetchInstagramStatus}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                                  >
                                    <RefreshCw className="w-3 h-3 mr-2" />
                                    Réessayer
                                  </Button>
                                </div>
                              )}
                              
                              {/* P0 FINAL: CASE_J - No scopes granted OR scopes not authoritative */}
                              {/* P0 DEFINITIVE: NEVER show this for AME auth errors or Meta unavailable */}
                              {igUnifiedStatus.ui_state !== 'ame_auth_required' && 
                               igUnifiedStatus.ui_state !== 'meta_unavailable' &&
                               (igUnifiedStatus.blocking_action === 'RECONNECT_WITH_REREQUEST' || 
                                igUnifiedStatus.ui_state === 'no_scopes_granted' ||
                                igUnifiedStatus.ui_state === 'scopes_not_authoritative') && (
                                <div className="space-y-2">
                                  <Button 
                                    type="button"
                                    size="sm" 
                                    onClick={handleInstagramConnect}
                                    disabled={igConnecting}
                                    className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold"
                                    data-testid="reconnect-rerequest-btn"
                                  >
                                    {igConnecting ? (
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                      <RefreshCw className="w-4 h-4 mr-2" />
                                    )}
                                    Reconnecter (rerequest)
                                  </Button>
                                  
                                  {/* Display missing scopes */}
                                  {igUnifiedStatus.missing_scopes && igUnifiedStatus.missing_scopes.length > 0 && (
                                    <div className="bg-orange-50 border border-orange-200 rounded p-2 text-xs">
                                      <p className="font-medium text-orange-800 mb-1">Permissions requises:</p>
                                      <div className="flex flex-wrap gap-1">
                                        {igUnifiedStatus.missing_scopes.map((scope) => (
                                          <span 
                                            key={scope} 
                                            className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded"
                                          >
                                            {scope}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Warning message */}
                                  <p className="text-xs text-gray-500 text-center">
                                    ⚠️ Acceptez <strong>toutes</strong> les permissions lors de la reconnexion
                                  </p>
                                </div>
                              )}
                              
                              {igUnifiedStatus.blocking_action === 'VALIDATE_PUBLISH' && (
                                <Button 
                                  type="button"
                                  size="sm" 
                                  onClick={handleValidateInstagramPublish}
                                  disabled={igTesting}
                                  className="w-full bg-green-500 hover:bg-green-600"
                                >
                                  {igTesting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                                  Valider publication (dry-run)
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Permissions Status (NEW) */}
                      {igUnifiedStatus && igUnifiedStatus.oauth_connected && (
                        <div className="mt-2 text-xs">
                          <p className="font-medium text-gray-600 mb-1">Permissions accordées:</p>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(igUnifiedStatus.permissions || {}).map(([perm, granted]) => (
                              <span 
                                key={perm} 
                                className={`px-2 py-0.5 rounded text-xs ${
                                  granted 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {granted ? '✓' : '✗'} {perm}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* PROJECT-SPECIFIC INSTAGRAM ACTIVATION */}
                      {igUnifiedStatus && igUnifiedStatus.oauth_connected && igUnifiedStatus.integration_state === 'OK' && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <Zap className="w-4 h-4 text-yellow-500" />
                            Publication pour ce projet
                          </h4>
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between bg-gray-50 rounded p-2">
                              <span className="text-xs text-gray-600">Publication manuelle</span>
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                                ✓ Activée
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between bg-gray-50 rounded p-2">
                              <span className="text-xs text-gray-600">Publication automatique</span>
                              {project?.channels_config?.instagram?.auto_publish ? (
                                <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                                  ✓ Activée
                                </Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEnableInstagramForProject(true)}
                                  disabled={igAutoPublishEnabling}
                                  className="text-xs h-6"
                                  data-testid="enable-ig-auto-publish-btn"
                                >
                                  {igAutoPublishEnabling ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <Zap className="w-3 h-3 mr-1" />
                                  )}
                                  Activer
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* META APP REVIEW CHECK SECTION */}
                      {igUnifiedStatus && (igUnifiedStatus.oauth_connected || igUnifiedStatus.user_token_ok) && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              <Shield className="w-4 h-4 text-blue-500" />
                              Meta App Review
                            </h4>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleAppReviewCheck}
                              disabled={igAppReviewLoading}
                              data-testid="meta-app-review-check-btn"
                              className="text-xs"
                            >
                              {igAppReviewLoading ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <CheckCircle className="w-3 h-3 mr-1" />
                              )}
                              Vérifier les prérequis
                            </Button>
                          </div>
                          
                          {/* Quick status indicator */}
                          {igAppReviewCheck && (
                            <div className={`text-xs p-2 rounded ${
                              igAppReviewCheck.ready_for_app_review 
                                ? 'bg-green-50 text-green-700 border border-green-200' 
                                : 'bg-amber-50 text-amber-700 border border-amber-200'
                            }`}>
                              {igAppReviewCheck.ready_for_app_review ? (
                                <span className="flex items-center gap-1 flex-wrap">
                                  <CheckCircle className="w-3 h-3" />
                                  Prêt pour App Review !
                                  {igAppReviewCheck.actions?.find(a => a.id === 'open_meta_app_review') && (
                                    <a
                                      href={igAppReviewCheck.actions.find(a => a.id === 'open_meta_app_review').url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="ml-2 underline font-medium text-blue-600 hover:text-blue-800"
                                      data-testid="open-meta-app-review-link"
                                    >
                                      Ouvrir Meta App Review →
                                    </a>
                                  )}
                                </span>
                              ) : (
                                <div className="space-y-2">
                                  <span className="flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    {igAppReviewCheck.blocking_message}
                                  </span>
                                  
                                  {/* POINT 1: CTA visible when instagram_content_publish is missing OR blocking_action is REQUEST_APP_REVIEW */}
                                  {(igAppReviewCheck.blocking_action === 'REQUEST_APP_REVIEW' || 
                                    igAppReviewCheck.checks?.permissions?.missing?.includes('instagram_content_publish')) && (
                                    <div className="flex items-center gap-2 mt-1">
                                      {igAppReviewCheck.actions?.find(a => a.id === 'open_meta_app_review') && (
                                        <a
                                          href={igAppReviewCheck.actions.find(a => a.id === 'open_meta_app_review').url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                          data-testid="open-meta-app-review-link"
                                        >
                                          <ExternalLink className="w-3 h-3" />
                                          Ouvrir Meta App Review
                                        </a>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* POINT 2: Settings Basic link when mode is UNKNOWN */}
                                  {igAppReviewCheck.checks?.meta_app?.mode === 'UNKNOWN' && (
                                    <div className="flex items-center gap-2 mt-1 pt-1 border-t border-amber-200">
                                      <span className="text-amber-600">⚠️ Mode App non vérifié</span>
                                      {igAppReviewCheck.actions?.find(a => a.id === 'open_meta_basic_settings') && (
                                        <a
                                          href={igAppReviewCheck.actions.find(a => a.id === 'open_meta_basic_settings').url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1 px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                          data-testid="open-meta-settings-link"
                                        >
                                          <ExternalLink className="w-3 h-3" />
                                          Settings → Basic
                                        </a>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* AUTH ERROR BANNER */}
                  {igError && (
                    <div className={`border rounded-lg p-4 ${
                      igError.next_action === 'LOGIN' ? 'bg-yellow-50 border-yellow-200' : 
                      igError.error === 'WRONG_APP_ID' || igError.error === 'INVALID_APP_ID' ? 'bg-orange-50 border-orange-200' :
                      'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-start gap-3">
                        <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                          igError.next_action === 'LOGIN' ? 'text-yellow-600' : 
                          igError.error === 'WRONG_APP_ID' || igError.error === 'INVALID_APP_ID' ? 'text-orange-600' :
                          'text-red-600'
                        }`} />
                        <div className="flex-1">
                          <h4 className={`font-medium ${
                            igError.next_action === 'LOGIN' ? 'text-yellow-800' : 
                            igError.error === 'WRONG_APP_ID' || igError.error === 'INVALID_APP_ID' ? 'text-orange-800' :
                            'text-red-800'
                          }`}>
                            {/* Map error codes to clear, actionable titles */}
                            {igError.next_action === 'LOGIN' ? 'Authentification requise' : 
                             igError.error === 'WRONG_APP_ID' ? 'Mauvais App ID' :
                             igError.error === 'INVALID_APP_ID' ? 'App ID invalide' :
                             igError.error === 'INVALID_APP_ID_FORMAT' ? 'Format App ID incorrect' :
                             igError.error === 'MISCONFIGURED' ? 'App Meta non configurée' :
                             igError.error === 'INVALID_SCOPES' ? 'Permissions invalides' :
                             igError.error === 'TOKEN_ERROR' ? 'Erreur de token' :
                             igError.error === 'GRAPH_ERROR' ? 'Erreur Graph API' :
                             igError.error === 'RATE_LIMITED' ? 'Action en cours' :
                             'Erreur de configuration'}
                          </h4>
                          <p className={`text-sm mt-1 ${
                            igError.next_action === 'LOGIN' ? 'text-yellow-700' : 
                            igError.error === 'WRONG_APP_ID' || igError.error === 'INVALID_APP_ID' ? 'text-orange-700' :
                            'text-red-700'
                          }`}>
                            {igError.message}
                          </p>
                          {/* Show hint for App ID errors */}
                          {igError.hint && (
                            <div className="mt-2 p-2 bg-white rounded border">
                              <p className="text-xs font-medium text-gray-700">💡 Solution:</p>
                              <p className="text-xs text-gray-600">{igError.hint}</p>
                              {igError.expected_id && (
                                <div className="mt-1 flex items-center gap-2">
                                  <code className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                    {igError.expected_id}
                                  </code>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 px-1"
                                    onClick={() => {
                                      navigator.clipboard.writeText(igError.expected_id);
                                      toast.success('App ID copié !');
                                    }}
                                  >
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                          {igError.request_id && (
                            <p className="text-xs text-gray-500 mt-2 font-mono">
                              Code: {igError.request_id}
                            </p>
                          )}
                          {igError.next_action === 'LOGIN' && (
                            <Button 
                              onClick={() => navigate('/auth')}
                              size="sm"
                              className="mt-3 bg-yellow-600 hover:bg-yellow-700"
                            >
                              Se connecter
                            </Button>
                          )}
                          {igError.next_action !== 'LOGIN' && (
                            <Button 
                              onClick={() => setIgError(null)}
                              size="sm"
                              variant="outline"
                              className="mt-3"
                            >
                              Fermer
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* APP CONFIGURATION */}
                  <div className="space-y-3 border-t pt-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium">Configurer Meta App</span>
                        <p className="text-xs text-muted-foreground">Requis pour connecter Instagram via Facebook OAuth</p>
                      </div>
                      <Switch 
                        checked={igUseCustomApp}
                        onCheckedChange={(checked) => {
                          setIgUseCustomApp(checked);
                          if (!checked) {
                            setIgAppId('');
                            setIgAppSecret('');
                          }
                        }}
                        data-testid="ig-use-custom-app-toggle"
                      />
                    </div>
                    
                    {/* App Config Source Badge */}
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">Source:</span>
                      <Badge className={`text-xs ${
                        igDiag?.app_config_source === 'CUSTOM' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {igDiag?.app_config_source === 'CUSTOM' ? '🔑 App configurée' : '⚠️ Non configurée'}
                      </Badge>
                      {igDiag?.has_custom_app_id && (
                        <Badge variant="outline" className="text-xs">ID: ✓</Badge>
                      )}
                      {igDiag?.has_custom_app_secret && (
                        <Badge variant="outline" className="text-xs">Secret: ✓</Badge>
                      )}
                    </div>
                    
                    {/* Custom App Fields */}
                    {igUseCustomApp && (
                      <div className="space-y-4 pt-2 border-t">
                        {/* IMPORTANT WARNING */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <HelpCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="text-xs text-blue-800">
                              <p className="font-semibold mb-1">⚠️ Important: Utilisez le FACEBOOK App ID</p>
                              <p>Pour OAuth, utilisez l&apos;ID de votre <strong>Facebook App</strong> (généralement commence par &quot;12...&quot;), PAS l&apos;Instagram App ID (commence par &quot;41...&quot;).</p>
                              <p className="mt-1">Trouvez-le dans Meta Developers → Votre App → Paramètres → Base.</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* FACEBOOK OAUTH CREDENTIALS */}
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                              <span className="text-white text-lg">f</span>
                            </div>
                            <div>
                              <h4 className="font-medium text-indigo-900">Facebook App (OAuth)</h4>
                              <p className="text-xs text-indigo-700">Requis pour la connexion</p>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="ig-app-id" className="text-xs font-medium text-indigo-900">
                              Facebook App ID <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id="ig-app-id"
                              placeholder="Ex: 1222771959703084"
                              value={igAppId}
                              onChange={(e) => setIgAppId(e.target.value)}
                              className="font-mono text-sm bg-white"
                              data-testid="ig-app-id-input"
                            />
                            {igDiag?.has_custom_app_id && !igAppId && (
                              <p className="text-xs text-green-600">✓ App ID déjà configuré</p>
                            )}
                            {igAppId && igAppId.startsWith('41') && (
                              <p className="text-xs text-orange-600">⚠️ Cet ID ressemble à un Instagram App ID. Vérifiez que c&apos;est bien le Facebook App ID.</p>
                            )}
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="ig-app-secret" className="text-xs font-medium text-indigo-900">
                              Facebook App Secret <span className="text-red-500">*</span>
                            </Label>
                            <div className="relative">
                              <Input
                                id="ig-app-secret"
                                type={showIgSecret ? "text" : "password"}
                                placeholder={igDiag?.has_custom_app_secret ? "••••••• (déjà configuré)" : "Entrez le secret"}
                                value={igAppSecret}
                                onChange={(e) => setIgAppSecret(e.target.value)}
                                className="font-mono text-sm pr-10 bg-white"
                                data-testid="ig-app-secret-input"
                              />
                              <button
                                type="button"
                                onClick={() => setShowIgSecret(!showIgSecret)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                              >
                                {showIgSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                            {igDiag?.has_custom_app_secret && !igAppSecret && (
                              <p className="text-xs text-green-600">✓ Secret déjà configuré (laisser vide pour conserver)</p>
                            )}
                          </div>
                        </div>
                        
                        {/* REDIRECT URI INFO */}
                        <div className="bg-gray-50 border rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-700 mb-2">📋 URL de redirection OAuth UNIQUE (à ajouter dans Meta)</p>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs bg-white px-2 py-1.5 rounded border font-mono text-gray-800 break-all">
                              https://ai-marketing-engine.com/api/auth/facebook/callback
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => {
                                navigator.clipboard.writeText('https://ai-marketing-engine.com/api/auth/facebook/callback');
                                toast.success('URL copiée !');
                              }}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-xs text-green-600 mt-2 font-medium">
                            ✅ UN SEUL callback pour Facebook ET Instagram !
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Ajoutez cette URL exacte dans: Meta Developers → Facebook Login → Settings → Valid OAuth Redirect URIs
                          </p>
                        </div>
                        
                        <Button 
                          onClick={handleSaveInstagramConfig}
                          disabled={igSaving || (!igAppId && !igDiag?.has_custom_app_id)}
                          className="w-full"
                          data-testid="ig-save-config-btn"
                        >
                          {igSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                          Sauvegarder la configuration
                        </Button>
                      </div>
                    )}
                    
                    {/* ACTION BUTTONS */}
                    {/* Connect button - show when app is configured but not connected */}
                    {(igDiag?.app_config_source === 'CUSTOM') && 
                     (!igDiag?.selected_ig_id && igDiag?.status !== 'OAUTH_DONE') && 
                     igDiag?.status !== 'WRONG_OAUTH_FLOW' && igDiag?.status !== 'MISSING_PERMISSIONS' && (
                      <Button 
                        type="button"
                        onClick={handleInstagramConnect} 
                        disabled={igConnecting || (igUseCustomApp && !igDiag?.has_custom_app_id)}
                        className="w-full bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 hover:from-yellow-500 hover:via-pink-600 hover:to-purple-700"
                        data-testid="ig-connect-btn"
                      >
                        {igConnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                        Connecter Instagram (via Facebook)
                      </Button>
                    )}
                    
                    {/* Reconnect button - show when WRONG_OAUTH_FLOW or MISSING_PERMISSIONS */}
                    {(igDiag?.status === 'WRONG_OAUTH_FLOW' || igDiag?.status === 'MISSING_PERMISSIONS') && (
                      <div className="space-y-2">
                        <div className="bg-red-50 border border-red-200 rounded p-3">
                          <p className="text-sm text-red-800 font-medium">
                            {igDiag.status === 'WRONG_OAUTH_FLOW' 
                              ? '⚠️ Token Instagram-only détecté' 
                              : '🔐 Permissions manquantes'}
                          </p>
                          <p className="text-xs text-red-600 mt-1">{igDiag.message}</p>
                          {igDiag.permissions_missing && igDiag.permissions_missing.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-red-500">Permissions manquantes:</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {igDiag.permissions_missing.map((perm, i) => (
                                  <span key={i} className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded">{perm}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <Button 
                          type="button"
                          onClick={handleInstagramConnect} 
                          disabled={igConnecting}
                          className="w-full bg-red-500 hover:bg-red-600"
                        >
                          {igConnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                          Reconnecter via Facebook
                        </Button>
                      </div>
                    )}
                    
                    {/* Select Account button - show when OAuth is done but no account selected */}
                    {/* OR when blocking_reason is SELECT_PAGE_REQUIRED */}
                    {(igDiag?.status === 'OAUTH_DONE' && !igDiag?.selected_ig_id) || 
                     (igUnifiedStatus?.blocking_reason === 'SELECT_PAGE_REQUIRED') ? (
                      <div className="space-y-2">
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                          <p className="font-medium text-yellow-800 mb-1">
                            🔔 Sélection requise
                          </p>
                          <p className="text-xs text-yellow-700">
                            {igUnifiedStatus?.blocking_message || 'Choisissez la Page Facebook liée à votre compte Instagram Business.'}
                          </p>
                          {igUnifiedStatus?.ig_accounts_count > 0 && (
                            <p className="text-xs text-yellow-600 mt-1">
                              {igUnifiedStatus.ig_accounts_count} compte(s) Instagram disponible(s) sur {igUnifiedStatus.pages_count} Page(s)
                            </p>
                          )}
                        </div>
                        <Button 
                          onClick={() => {
                            fetchInstagramAccounts();
                            setIgAccountModalOpen(true);
                          }} 
                          disabled={igLoadingAccounts} 
                          className="w-full bg-yellow-500 hover:bg-yellow-600"
                          data-testid="ig-select-page-btn"
                        >
                          {igLoadingAccounts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                          Sélectionner une Page Facebook
                        </Button>
                      </div>
                    ) : null}
                    
                    {/* Validate Publish button - show when CONNECTED but not POST_READY */}
                    {igDiag?.status === 'CONNECTED' && !igDiag?.post_ready && (
                      <div className="space-y-2">
                        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-700">
                          💡 Compte sélectionné. Validez la capacité de publication (dry-run sans post visible).
                        </div>
                        <Button 
                          type="button"
                          onClick={handleValidateInstagramPublish} 
                          disabled={igTesting}
                          className="w-full bg-blue-500 hover:bg-blue-600"
                        >
                          {igTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                          Valider publication (dry-run)
                        </Button>
                      </div>
                    )}
                    
                    {/* POST_READY state - Test, Change Account, Disconnect */}
                    {(igDiag?.status === 'POST_READY' || igUnifiedStatus?.publish_ready) && (
                      <div className="space-y-2">
                        <div className="bg-green-50 border border-green-200 rounded p-2 text-xs text-green-700">
                          ✅ Prêt à publier sur @{igUnifiedStatus?.ig_username || igDiag?.ig_username || 'Instagram'} !
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            type="button"
                            onClick={handleInstagramTest} 
                            disabled={igTesting} 
                            className="flex-1 bg-green-500 hover:bg-green-600"
                          >
                            {igTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                            Publier test
                          </Button>
                          <Button 
                            type="button"
                            variant="outline" 
                            onClick={() => {
                              fetchInstagramAccounts();
                              setIgAccountModalOpen(true);
                            }} 
                            className="flex-1"
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Changer compte
                          </Button>
                        </div>
                        <Button 
                          type="button"
                          variant="outline" 
                          onClick={() => setIgDisconnectModalOpen(true)}
                          className="w-full text-red-600 border-red-200 hover:bg-red-50"
                          data-testid="ig-disconnect-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Déconnecter Instagram
                        </Button>
                        
                        {/* Debug Bundle - Toujours accessible en POST_READY */}
                        <Button 
                          variant="ghost" 
                          onClick={handleCopyDebugBundle}
                          disabled={metaDebugLoading}
                          className="w-full text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 mt-2"
                          data-testid="meta-debug-copy-btn-postready"
                        >
                          {metaDebugLoading ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Copy className="w-3 h-3 mr-1" />
                          )}
                          Copier Debug Bundle (support)
                        </Button>
                      </div>
                    )}
                    
                    {/* NO_FACEBOOK_PAGE state - User needs to create/become admin of a FB Page */}
                    {igDiag?.status === 'NO_FACEBOOK_PAGE' && (
                      <div className="space-y-2">
                        <div className="bg-orange-50 border border-orange-200 rounded p-3">
                          <p className="text-sm text-orange-800 font-medium">📄 Aucune Page Facebook trouvée</p>
                          <p className="text-xs text-orange-600 mt-1">{igDiag.message}</p>
                          <p className="text-xs text-orange-700 mt-2">
                            Pour utiliser l&apos;API Instagram Business, vous devez être administrateur d&apos;au moins une Page Facebook.
                          </p>
                        </div>
                        <a 
                          href="https://www.facebook.com/pages/creation/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <Button type="button" variant="outline" className="w-full border-orange-300 text-orange-700 hover:bg-orange-50">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Créer une Page Facebook
                          </Button>
                        </a>
                        <Button 
                          type="button"
                          onClick={handleInstagramConnect} 
                          disabled={igConnecting}
                          className="w-full"
                        >
                          {igConnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                          Réessayer après avoir créé la Page
                        </Button>
                      </div>
                    )}
                    
                    {/* NO_IG_BUSINESS_ACCOUNT state - User needs to convert IG to Business and link to FB Page */}
                    {igDiag?.status === 'NO_IG_BUSINESS_ACCOUNT' && (
                      <div className="space-y-2">
                        <div className="bg-orange-50 border border-orange-200 rounded p-3">
                          <p className="text-sm text-orange-800 font-medium">📷 Aucun compte Instagram Business trouvé</p>
                          <p className="text-xs text-orange-600 mt-1">{igDiag.message}</p>
                          <div className="text-xs text-orange-700 mt-2 space-y-1">
                            <p><strong>Étapes requises:</strong></p>
                            <ol className="list-decimal ml-4">
                              <li>Sur l&apos;app Instagram mobile: Paramètres → Compte → Passer à un compte professionnel</li>
                              <li>Choisissez Business ou Créateur</li>
                              <li>Liez ce compte à votre Page Facebook (dont vous êtes admin)</li>
                            </ol>
                          </div>
                        </div>
                        <Button 
                          type="button"
                          onClick={handleInstagramConnect} 
                          disabled={igConnecting}
                          className="w-full"
                        >
                          {igConnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                          Réessayer après configuration
                        </Button>
                        <Button 
                          variant="outline" 
                          onClick={() => setIgDisconnectModalOpen(true)}
                          className="w-full text-red-600 border-red-200 hover:bg-red-50"
                          data-testid="ig-disconnect-btn-no-ig"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Déconnecter et recommencer
                        </Button>
                      </div>
                    )}
                    
                    {/* PUBLISH_VALIDATION_FAILED state - Dry-run failed */}
                    {igDiag?.status === 'PUBLISH_VALIDATION_FAILED' && (
                      <div className="space-y-2">
                        <div className="bg-red-50 border border-red-200 rounded p-3">
                          <p className="text-sm text-red-800 font-medium">❌ Validation de publication échouée</p>
                          <p className="text-xs text-red-600 mt-1">{igDiag.message}</p>
                          {igDiag.last_graph_error && (
                            <p className="text-xs text-red-500 mt-1">
                              Erreur: {igDiag.last_graph_error.message || JSON.stringify(igDiag.last_graph_error)}
                            </p>
                          )}
                        </div>
                        <Button 
                          type="button"
                          onClick={handleValidateInstagramPublish} 
                          disabled={igTesting}
                          className="w-full"
                        >
                          {igTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                          Réessayer la validation
                        </Button>
                        <Button 
                          type="button"
                          onClick={handleInstagramConnect} 
                          disabled={igConnecting}
                          variant="outline"
                          className="w-full"
                        >
                          {igConnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                          Reconnecter avec permissions complètes
                        </Button>
                      </div>
                    )}
                    
                    {/* CONNECTED but needs validation state - legacy/fallback */}
                    {(igDiag?.status === 'READY' || igDiag?.status === 'ACCOUNT_SELECTED') && igDiag?.status !== 'POST_READY' && !igDiag?.post_ready && (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Button type="button" onClick={handleValidateInstagramPublish} disabled={igTesting} className="flex-1">
                            {igTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                            Valider
                          </Button>
                          <Button 
                            onClick={() => {
                              fetchInstagramAccounts();
                              setIgAccountModalOpen(true);
                            }} 
                            disabled={igLoadingAccounts}
                            variant="outline" 
                            className="flex-1"
                          >
                            {igLoadingAccounts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                            Changer compte
                          </Button>
                        </div>
                        <Button 
                          variant="outline" 
                          onClick={() => setIgDisconnectModalOpen(true)}
                          className="w-full text-red-600 border-red-200 hover:bg-red-50"
                          data-testid="ig-disconnect-btn-validation-failed"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Déconnecter Instagram
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* ZONE DE DANGER - Always visible when partially connected */}
                  {/* META DEBUG BUNDLE v2.1 - Only visible with ?debug=1 */}
                  {isDebugMode && (
                    <div className="mt-6 border-t border-blue-100 pt-4">
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
                          🔍 Debug Bundle v2.1 (Mode développeur)
                        </h4>
                        <p className="text-xs text-blue-600 mb-3">
                          Génère un bundle de diagnostic complet pour troubleshooting. 
                          Aucun secret exposé (uniquement les fingerprints de tokens).
                        </p>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            onClick={handleCopyDebugBundle}
                            disabled={metaDebugLoading}
                            className="flex-1 text-blue-600 border-blue-300 hover:bg-blue-100"
                            data-testid="meta-debug-copy-btn"
                          >
                            {metaDebugLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Génération...
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4 mr-2" />
                                Copier Debug Bundle
                              </>
                            )}
                          </Button>
                        </div>
                        {metaDebugBundle && (
                          <div className="mt-3 p-3 bg-gray-900 rounded text-xs font-mono overflow-auto">
                            {/* Integration State Badge */}
                            <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-700">
                              <span className="text-gray-400">État d'intégration:</span>
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                metaDebugBundle.integration_state === 'OK' 
                                  ? 'bg-green-600 text-white' 
                                  : metaDebugBundle.integration_state === 'NEEDS_RECONNECT'
                                  ? 'bg-orange-500 text-white'
                                  : metaDebugBundle.integration_state === 'NEEDS_META_ACTION'
                                  ? 'bg-yellow-500 text-black'
                                  : metaDebugBundle.integration_state === 'NOT_CONFIGURED'
                                  ? 'bg-gray-500 text-white'
                                  : 'bg-red-600 text-white'
                              }`}>
                                {metaDebugBundle.integration_state}
                              </span>
                            </div>
                            
                            {/* State Message */}
                            {metaDebugBundle.integration_state_message && (
                              <div className={`mb-2 p-2 rounded ${
                                metaDebugBundle.integration_state === 'OK' 
                                  ? 'bg-green-900/30 text-green-300' 
                                  : 'bg-yellow-900/30 text-yellow-300'
                              }`}>
                                {metaDebugBundle.integration_state === 'OK' ? '✅' : '⚠️'} {metaDebugBundle.integration_state_message}
                              </div>
                            )}
                            
                            {/* Actions Required */}
                            {metaDebugBundle.integration_state_actions && metaDebugBundle.integration_state_actions.length > 0 && (
                              <div className="mb-2 p-2 bg-blue-900/30 rounded">
                                <div className="text-blue-300 font-bold mb-1">Actions requises:</div>
                                <ul className="text-blue-200 list-disc list-inside">
                                  {metaDebugBundle.integration_state_actions.map((action, idx) => (
                                    <li key={idx}>{action.replace(/_/g, ' ')}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Classification Details */}
                            <div className="flex justify-between items-center text-gray-400 text-xs mt-2 pt-2 border-t border-gray-700">
                              <span>Bundle: {metaDebugBundle.bundle_id}</span>
                              <span>Case: <strong className={metaDebugBundle.error_classification === 'OK' ? 'text-green-400' : 'text-yellow-400'}>{metaDebugBundle.error_classification}</strong></span>
                            </div>
                            
                            {/* Case Candidate (if present) */}
                            {metaDebugBundle.case_candidate && (
                              <div className="mt-2 text-purple-300 text-xs">
                                🔮 Cas probable: {metaDebugBundle.case_candidate} ({metaDebugBundle.case_candidate_evidence})
                              </div>
                            )}
                            
                            {/* Error Hypothesis */}
                            {metaDebugBundle.error_hypothesis && metaDebugBundle.error_classification !== 'OK' && (
                              <div className="text-yellow-300 mt-2 text-xs">
                                📋 {metaDebugBundle.error_hypothesis}
                              </div>
                            )}
                            
                            {/* Recommended Action */}
                            {metaDebugBundle.recommended_action && metaDebugBundle.error_classification !== 'OK' && (
                              <div className="text-blue-300 mt-1 text-xs">
                                💡 {metaDebugBundle.recommended_action}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* P0 FINAL: Version Info & Hard Refresh */}
                        <div className="mt-4 pt-4 border-t border-blue-200">
                          <h5 className="text-xs font-medium text-blue-700 mb-2">📦 Version Backend</h5>
                          {backendVersion ? (
                            <div className="flex items-center justify-between text-xs">
                              <div className="flex gap-3 text-gray-600">
                                <span>Git: <code className="bg-gray-100 px-1 rounded">{backendVersion.git_sha_short}</code></span>
                                <span>Build: <code className="bg-gray-100 px-1 rounded">{backendVersion.build_time?.substring(0, 19)}</code></span>
                                <span>v{backendVersion.engine_version}</span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleHardRefresh}
                                className="text-blue-600 hover:bg-blue-100 text-xs h-7"
                                data-testid="hard-refresh-btn"
                              >
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Hard Refresh
                              </Button>
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400">Chargement version...</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {shouldShowIgDisconnect() && (
                    <div className="mt-6 border-t border-red-100 pt-4">
                      <div className="bg-red-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-red-800 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Zone de danger
                        </h4>
                        <p className="text-xs text-red-600 mb-3">
                          Réinitialisez la connexion Instagram pour recommencer proprement.
                        </p>
                        <Button 
                          variant="outline" 
                          onClick={() => setIgDisconnectModalOpen(true)}
                          className="w-full text-red-600 border-red-300 hover:bg-red-100"
                          data-testid="ig-disconnect-danger-zone-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Déconnecter Instagram (Hard Reset)
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* TUTORIAL BUTTON - Always visible */}
                  <div className="mt-3">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        fetchInstagramTutorial();
                        setIgTutorialOpen(true);
                      }}
                      className="w-full text-pink-600 border-pink-200 hover:bg-pink-50"
                    >
                      📚 Guide de configuration pas à pas
                    </Button>
                  </div>
                  
                  {/* APP REVIEW INFO - CORRECTED PERMISSIONS */}
                  <details className="text-xs bg-pink-50 rounded-lg border border-pink-200 mt-3">
                    <summary className="cursor-pointer p-2 font-medium text-pink-800">
                      ℹ️ Permissions requises (Facebook Login + Instagram Graph API)
                    </summary>
                    <div className="p-2 pt-0 text-pink-700 space-y-1">
                      <div className="bg-amber-50 border border-amber-300 rounded p-2 mb-2">
                        <p className="text-amber-800 text-xs font-medium">
                          ⚠️ Les permissions <code>instagram_business_*</code> n&apos;existent pas en OAuth Facebook.
                          Seules les permissions <code>instagram_*</code> sont valides.
                        </p>
                      </div>
                      <p>Ces permissions sont demandées lors de la connexion Facebook Login :</p>
                      <ul className="list-disc ml-4">
                        <li><code>pages_show_list</code> — Accès à vos Pages Facebook</li>
                        <li><code>pages_read_engagement</code> — Requis par Meta</li>
                        <li><code>instagram_basic</code> — Accès au compte Instagram</li>
                        <li><code>instagram_content_publish</code> — Publication de contenus</li>
                        <li><code>instagram_manage_insights</code> (optionnel) — Statistiques</li>
                        <li><code>instagram_manage_comments</code> (optionnel) — Commentaires</li>
                      </ul>
                      <p className="mt-2 text-yellow-700 bg-yellow-50 p-2 rounded">
                        ⚠️ <strong>Important :</strong> Votre compte Instagram doit être un compte <strong>Business</strong> ou <strong>Creator</strong> lié à une Page Facebook dont vous êtes <strong>admin</strong>.
                      </p>
                    </div>
                  </details>
                </div>
              </CardContent>
            </Card>

            {/* INSTAGRAM ACCOUNT SELECTION MODAL */}
            {igAccountModalOpen && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-lg">Sélectionner une Page Facebook</h3>
                    <button onClick={() => setIgAccountModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Info Banner */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-800">
                    <p className="font-medium mb-1">💡 Comment ça marche</p>
                    <p>Sélectionnez la Page Facebook liée à votre compte Instagram Business. Seules les Pages avec un compte IG lié (✅) peuvent publier.</p>
                  </div>
                  
                  {igLoadingAccounts ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
                    </div>
                  ) : igAccounts.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <p className="mb-2">Aucune Page Facebook trouvée.</p>
                      <p className="text-xs">Assurez-vous que vous êtes administrateur d&apos;au moins une Page Facebook.</p>
                      <ul className="text-xs text-left mt-4 space-y-1 bg-gray-50 p-3 rounded-lg">
                        <li>1. Créez ou rejoignez une Page Facebook</li>
                        <li>2. Passez votre Instagram en compte Business/Creator</li>
                        <li>3. Liez votre compte IG à la Page Facebook</li>
                        <li>4. Reconnectez Instagram via Facebook Login</li>
                      </ul>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Count summary */}
                      <div className="text-xs text-muted-foreground mb-2">
                        {igAccounts.filter(a => a.has_instagram).length} Page(s) avec Instagram Business sur {igAccounts.length} Page(s)
                      </div>
                      
                      {igAccounts.map((account) => (
                        <button
                          key={account.page_id}
                          onClick={() => handleSelectInstagramAccount(account.page_id)}
                          disabled={!account.has_page_token}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            account.has_instagram && account.has_page_token
                              ? 'border-pink-300 hover:border-pink-500 hover:bg-pink-50'
                              : account.has_page_token
                                ? 'border-orange-200 hover:border-orange-400 hover:bg-orange-50'
                                : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {account.ig_profile_picture_url ? (
                              <img 
                                src={account.ig_profile_picture_url} 
                                alt={account.ig_username}
                                className="w-10 h-10 rounded-full"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-500 text-lg">f</span>
                              </div>
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-sm">
                                {account.page_name}
                              </p>
                              {account.has_instagram && account.ig_username ? (
                                <p className="text-xs text-pink-600">
                                  @{account.ig_username}
                                  {account.ig_followers_count && ` • ${account.ig_followers_count} followers`}
                                </p>
                              ) : (
                                <p className="text-xs text-orange-600">
                                  Pas de compte IG lié
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              {account.has_instagram ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">✅ IG lié</Badge>
                              ) : (
                                <Badge className="bg-orange-100 text-orange-800 text-xs">⚠️ Pas d'IG</Badge>
                              )}
                              {!account.has_page_token && (
                                <Badge className="bg-red-100 text-red-800 text-xs">❌ Token</Badge>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-4 space-y-2">
                    {/* Show special refresh button when NO_IG_BUSINESS_ACCOUNT */}
                    {igAccounts.length > 0 && igAccounts.every(a => !a.has_instagram) && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs">
                        <p className="font-medium text-orange-800 mb-2">Vous avez récemment lié Instagram à une Page ?</p>
                        <Button 
                          variant="outline" 
                          onClick={handleRefreshInstagramAccounts} 
                          disabled={igLoadingAccounts}
                          className="w-full border-orange-300 text-orange-700 hover:bg-orange-100"
                          data-testid="ig-force-refresh-btn"
                        >
                          <RefreshCw className={`w-4 h-4 mr-2 ${igLoadingAccounts ? 'animate-spin' : ''}`} />
                          Rafraîchir depuis Facebook
                        </Button>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setIgAccountModalOpen(false)} className="flex-1">
                        Annuler
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={handleRefreshInstagramAccounts} 
                        disabled={igLoadingAccounts}
                        data-testid="ig-refresh-accounts-btn"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${igLoadingAccounts ? 'animate-spin' : ''}`} />
                        Rafraîchir
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* INSTAGRAM DISCONNECT CONFIRMATION MODAL */}
            {igDisconnectModalOpen && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="ig-disconnect-modal">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-lg text-red-600 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Déconnecter Instagram ?
                    </h3>
                    <button 
                      onClick={() => setIgDisconnectModalOpen(false)} 
                      className="text-gray-500 hover:text-gray-700"
                      disabled={igDisconnecting}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <p className="text-sm text-gray-700">
                      Cela supprime le token, la sélection de Page, le compte Instagram, et l'historique de connexion.
                    </p>
                    
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs">
                      <p className="font-medium text-red-800 mb-2">Cette action va supprimer :</p>
                      <ul className="list-disc ml-4 space-y-1 text-red-700">
                        <li>La connexion OAuth (User Token)</li>
                        <li>Le token de Page Facebook</li>
                        <li>Le compte Instagram sélectionné</li>
                        <li>Les autorisations accordées</li>
                      </ul>
                    </div>
                    
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs">
                      <p className="font-medium text-green-800 mb-1">✅ Ce qui sera conservé :</p>
                      <p className="text-green-700">Vos posts et statistiques publiés ne seront pas supprimés.</p>
                    </div>
                    
                    <p className="text-xs text-muted-foreground">
                      Vous devrez reconnecter Instagram via Facebook Login ensuite.
                    </p>
                  </div>
                  
                  <div className="mt-6 flex gap-3">
                    <Button 
                      type="button"
                      variant="outline" 
                      onClick={() => setIgDisconnectModalOpen(false)} 
                      disabled={igDisconnecting}
                      className="flex-1"
                      data-testid="ig-disconnect-cancel-btn"
                    >
                      Annuler
                    </Button>
                    <Button 
                      type="button"
                      onClick={handleInstagramDisconnect}
                      disabled={igDisconnecting}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      data-testid="ig-disconnect-confirm-btn"
                    >
                      {igDisconnecting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Déconnexion...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Déconnecter
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* OAUTH ACCESS DENIED HELP MODAL */}
            {showAccessDeniedHelp && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="access-denied-help-modal">
                <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-lg text-amber-600 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Accès refusé par Meta
                    </h3>
                    <button 
                      onClick={() => setShowAccessDeniedHelp(false)} 
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm text-amber-800">
                        L'autorisation a été refusée. Cela peut arriver si :
                      </p>
                      <ul className="list-disc ml-4 mt-2 space-y-1 text-sm text-amber-700">
                        <li>Vous avez annulé l'autorisation</li>
                        <li>Une ancienne intégration bloque la nouvelle</li>
                        <li>Les permissions ont été révoquées côté Facebook</li>
                      </ul>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="font-medium text-blue-800 mb-2">Pour résoudre ce problème :</p>
                      <ol className="list-decimal ml-4 space-y-2 text-sm text-blue-700">
                        <li>
                          <span className="font-medium">Supprimez l'intégration existante</span>
                          <p className="text-xs text-blue-600">Dans Business Integrations de Facebook</p>
                        </li>
                        <li>
                          <span className="font-medium">Supprimez l'app de votre compte</span>
                          <p className="text-xs text-blue-600">Dans Apps & Websites de Facebook</p>
                        </li>
                        <li>
                          <span className="font-medium">Attendez 5 minutes</span>
                          <p className="text-xs text-blue-600">Puis réessayez la connexion</p>
                        </li>
                      </ol>
                    </div>
                  </div>
                  
                  <div className="mt-6 space-y-3">
                    <div className="flex gap-3">
                      <Button 
                        variant="outline" 
                        onClick={() => window.open('https://business.facebook.com/settings/instagram-business-accounts', '_blank')}
                        className="flex-1 text-sm"
                        data-testid="open-business-integrations-btn"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Business Integrations
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => window.open('https://www.facebook.com/settings?tab=applications', '_blank')}
                        className="flex-1 text-sm"
                        data-testid="open-apps-websites-btn"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Apps & Websites
                      </Button>
                    </div>
                    <Button 
                      onClick={() => setShowAccessDeniedHelp(false)}
                      className="w-full"
                      data-testid="close-access-denied-help-btn"
                    >
                      Compris, je vais essayer
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* META APP REVIEW CHECK RESULTS MODAL */}
            {igAppReviewOpen && igAppReviewCheck && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="app-review-modal">
                <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-600" />
                      Meta App Review - Prérequis
                    </h3>
                    <button 
                      onClick={() => setIgAppReviewOpen(false)} 
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Status banner */}
                  <div className={`p-3 rounded-lg mb-4 ${
                    igAppReviewCheck.ready_for_app_review 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-amber-50 border border-amber-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      {igAppReviewCheck.ready_for_app_review ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-green-800">
                            ✅ Prêt pour Meta App Review !
                          </span>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="w-5 h-5 text-amber-600" />
                          <span className="font-medium text-amber-800">
                            {igAppReviewCheck.blocking_message}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Checks grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {/* Instagram Account Check */}
                    <div className={`border rounded-lg p-3 ${
                      igAppReviewCheck.checks?.instagram_account?.ok 
                        ? 'border-green-200 bg-green-50' 
                        : 'border-red-200 bg-red-50'
                    }`}>
                      <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                        <span className="p-1 rounded bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"/>
                          </svg>
                        </span>
                        Compte Instagram
                      </h4>
                      <ul className="text-xs space-y-1">
                        <li className="flex items-center gap-1">
                          {igAppReviewCheck.checks?.instagram_account?.ig_user_id_present ? 
                            <CheckCircle className="w-3 h-3 text-green-600" /> : 
                            <X className="w-3 h-3 text-red-600" />}
                          IG Business ID
                        </li>
                        <li className="flex items-center gap-1">
                          {igAppReviewCheck.checks?.instagram_account?.type === 'BUSINESS' || 
                           igAppReviewCheck.checks?.instagram_account?.type === 'CREATOR' ? 
                            <CheckCircle className="w-3 h-3 text-green-600" /> : 
                            <X className="w-3 h-3 text-red-600" />}
                          Type: {igAppReviewCheck.checks?.instagram_account?.type || 'UNKNOWN'}
                        </li>
                        <li className="flex items-center gap-1">
                          {igAppReviewCheck.checks?.instagram_account?.linked_page ? 
                            <CheckCircle className="w-3 h-3 text-green-600" /> : 
                            <X className="w-3 h-3 text-red-600" />}
                          Page liée
                        </li>
                        <li className="flex items-center gap-1">
                          {igAppReviewCheck.checks?.instagram_account?.page_is_admin ? 
                            <CheckCircle className="w-3 h-3 text-green-600" /> : 
                            <X className="w-3 h-3 text-red-600" />}
                          Admin Page
                        </li>
                      </ul>
                    </div>
                    
                    {/* Facebook User Check */}
                    <div className={`border rounded-lg p-3 ${
                      igAppReviewCheck.checks?.facebook_user?.ok 
                        ? 'border-green-200 bg-green-50' 
                        : 'border-red-200 bg-red-50'
                    }`}>
                      <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                        Utilisateur Facebook
                      </h4>
                      <ul className="text-xs space-y-1">
                        <li className="flex items-center gap-1">
                          {igAppReviewCheck.checks?.facebook_user?.is_page_admin ? 
                            <CheckCircle className="w-3 h-3 text-green-600" /> : 
                            <X className="w-3 h-3 text-red-600" />}
                          Admin de Page
                        </li>
                        <li className="flex items-center gap-1">
                          {igAppReviewCheck.checks?.facebook_user?.ok ? 
                            <CheckCircle className="w-3 h-3 text-green-600" /> : 
                            <X className="w-3 h-3 text-red-600" />}
                          Token utilisateur
                        </li>
                      </ul>
                    </div>
                    
                    {/* Meta App Check */}
                    <div className={`border rounded-lg p-3 ${
                      igAppReviewCheck.checks?.meta_app?.ok 
                        ? 'border-green-200 bg-green-50' 
                        : 'border-amber-200 bg-amber-50'
                    }`}>
                      <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                        <Settings className="w-4 h-4 text-gray-600" />
                        Meta App
                      </h4>
                      <ul className="text-xs space-y-1">
                        <li className="flex items-center gap-1">
                          {igAppReviewCheck.checks?.meta_app?.mode === 'LIVE' ? 
                            <CheckCircle className="w-3 h-3 text-green-600" /> : 
                            <AlertTriangle className="w-3 h-3 text-amber-600" />}
                          Mode: {igAppReviewCheck.checks?.meta_app?.mode || 'UNKNOWN'}
                        </li>
                        <li className="flex items-center gap-1">
                          {igAppReviewCheck.checks?.meta_app?.facebook_login_enabled ? 
                            <CheckCircle className="w-3 h-3 text-green-600" /> : 
                            <X className="w-3 h-3 text-red-600" />}
                          Facebook Login
                        </li>
                      </ul>
                      {igAppReviewCheck.checks?.meta_app?.app_review_hint && (
                        <p className="text-xs text-gray-500 mt-2 italic">
                          {igAppReviewCheck.checks?.meta_app?.app_review_hint}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Permissions section */}
                  <div className="border rounded-lg p-3 mb-4">
                    <h4 className="font-medium text-sm mb-2">Permissions</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs font-medium text-green-700 mb-1">Accordées:</p>
                        <div className="flex flex-wrap gap-1">
                          {igAppReviewCheck.checks?.permissions?.granted?.map(perm => (
                            <span key={perm} className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">
                              ✓ {perm}
                            </span>
                          ))}
                          {(!igAppReviewCheck.checks?.permissions?.granted || 
                            igAppReviewCheck.checks?.permissions?.granted?.length === 0) && (
                            <span className="text-xs text-gray-400">Aucune</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-red-700 mb-1">Manquantes:</p>
                        <div className="flex flex-wrap gap-1">
                          {igAppReviewCheck.checks?.permissions?.missing?.map(perm => (
                            <span key={perm} className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">
                              ✗ {perm}
                            </span>
                          ))}
                          {(!igAppReviewCheck.checks?.permissions?.missing || 
                            igAppReviewCheck.checks?.permissions?.missing?.length === 0) && (
                            <span className="text-xs text-green-600">Aucune ✓</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {igAppReviewCheck.actions?.map(action => (
                      <Button
                        key={action.id}
                        size="sm"
                        variant={action.type === 'link' ? 'outline' : 'default'}
                        onClick={() => handleAppReviewAction(action)}
                        data-testid={`app-review-action-${action.id}`}
                        className={action.id === 'open_meta_app_review' && igAppReviewCheck.ready_for_app_review
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : ''
                        }
                      >
                        {action.type === 'link' && <ExternalLink className="w-3 h-3 mr-1" />}
                        {action.label}
                      </Button>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIgAppReviewOpen(false)}
                    >
                      Fermer
                    </Button>
                  </div>
                  
                  {/* Debug info */}
                  <details className="mt-4 text-xs text-gray-400">
                    <summary className="cursor-pointer">Debug info</summary>
                    <pre className="bg-gray-100 p-2 rounded mt-1 overflow-x-auto text-xs">
                      request_id: {igAppReviewCheck.request_id}
                    </pre>
                  </details>
                </div>
              </div>
            )}

            {/* Instagram Info Modal */}
            {igInfoOpen && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <span className="p-1.5 rounded bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
                        </svg>
                      </span>
                      Instagram Feed Posts
                    </h3>
                    <button onClick={() => setIgInfoOpen(false)} className="text-gray-500 hover:text-gray-700">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="space-y-4 text-sm">
                    <p>
                      Publiez automatiquement des <strong>images</strong> sur votre compte Instagram Business ou Creator.
                    </p>
                    <div className="bg-pink-50 rounded p-3">
                      <h4 className="font-medium mb-2">Pré-requis :</h4>
                      <ul className="list-disc ml-4 space-y-1 text-xs">
                        <li>Un compte Instagram <strong>Business</strong> ou <strong>Creator</strong></li>
                        <li>Une <strong>Page Facebook</strong> liée à ce compte</li>
                        <li>Être <strong>admin</strong> de cette Page Facebook</li>
                        <li>Une <strong>App Meta</strong> avec les permissions Instagram</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 rounded p-3">
                      <h4 className="font-medium mb-2">Fonctionnalités :</h4>
                      <ul className="list-disc ml-4 space-y-1 text-xs">
                        <li>✅ Publication d'images (Feed Posts)</li>
                        <li>✅ Captions avec hashtags et CTA</li>
                        <li>❌ Stories (non supporté - API limitée)</li>
                        <li>❌ Reels (non supporté)</li>
                        <li>❌ Carrousels (à venir)</li>
                      </ul>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Instagram nécessite une URL d'image <strong>HTTPS publiquement accessible</strong> pour la publication.
                    </p>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button variant="outline" onClick={() => {
                      setIgInfoOpen(false);
                      fetchInstagramTutorial();
                      setIgTutorialOpen(true);
                    }} className="flex-1">
                      📚 Voir le tutoriel complet
                    </Button>
                    <Button onClick={() => setIgInfoOpen(false)} className="flex-1">
                      Compris
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Instagram Tutorial Modal */}
            {igTutorialOpen && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
                <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-4 sticky top-0 bg-white pb-2 border-b">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      📚 Guide de connexion Instagram Business
                    </h3>
                    <button onClick={() => setIgTutorialOpen(false)} className="text-gray-500 hover:text-gray-700">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {igTutorial ? (
                    <div className="space-y-6">
                      {/* Estimated time */}
                      <div className="bg-pink-50 rounded-lg p-3 text-center">
                        <p className="text-sm text-pink-800">
                          ⏱️ Temps estimé: <strong>{igTutorial.estimated_time}</strong>
                        </p>
                      </div>
                      
                      {/* Prerequisites */}
                      <div>
                        <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                          📋 Prérequis
                        </h4>
                        <div className="space-y-4">
                          {igTutorial.prerequisites?.map((prereq, index) => (
                            <div key={index} className="bg-gray-50 rounded-lg p-4 border-l-4 border-pink-500">
                              <h5 className="font-medium text-base mb-2">
                                {prereq.step}. {prereq.title}
                              </h5>
                              <p className="text-sm text-gray-600 mb-3">{prereq.description}</p>
                              
                              <div className="bg-white rounded p-3 mb-2">
                                <p className="text-xs font-medium text-gray-500 mb-2">Comment faire :</p>
                                <ol className="list-decimal ml-4 text-sm space-y-1">
                                  {prereq.how_to?.map((step, i) => (
                                    <li key={i}>{step}</li>
                                  ))}
                                </ol>
                              </div>
                              
                              {prereq.important && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800">
                                  ⚠️ <strong>Important:</strong> {prereq.important}
                                </div>
                              )}
                              
                              {prereq.permissions_needed && (
                                <div className="mt-2">
                                  <p className="text-xs font-medium text-gray-500 mb-1">Permissions requises:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {prereq.permissions_needed.map((perm, i) => (
                                      <span key={i} className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded">
                                        {perm}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Connection Steps */}
                      <div>
                        <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                          🔗 Étapes de connexion
                        </h4>
                        <div className="space-y-3">
                          {igTutorial.connection_steps?.map((step, index) => (
                            <div key={index} className="flex gap-3 items-start">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                {step.step}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-sm">{step.action}</p>
                                {step.where && <p className="text-xs text-gray-500">📍 {step.where}</p>}
                                {step.what_happens && <p className="text-xs text-gray-500">💡 {step.what_happens}</p>}
                                {step.important && <p className="text-xs text-yellow-600">⚠️ {step.important}</p>}
                                {step.how && <p className="text-xs text-gray-500">👆 {step.how}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Troubleshooting */}
                      <div>
                        <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                          🔧 Résolution de problèmes
                        </h4>
                        <div className="space-y-3">
                          {igTutorial.troubleshooting?.map((item, index) => (
                            <details key={index} className="bg-red-50 rounded-lg border border-red-200">
                              <summary className="cursor-pointer p-3 font-medium text-sm text-red-800">
                                ❌ {item.problem}
                              </summary>
                              <div className="px-3 pb-3">
                                <p className="text-xs text-gray-500 mb-2">Causes possibles:</p>
                                <ul className="list-disc ml-4 text-sm text-red-700 space-y-1 mb-2">
                                  {item.causes?.map((cause, i) => (
                                    <li key={i}>{cause}</li>
                                  ))}
                                </ul>
                                <p className="text-xs bg-green-50 border border-green-200 rounded p-2 text-green-800">
                                  ✅ <strong>Solution:</strong> {item.solution}
                                </p>
                              </div>
                            </details>
                          ))}
                        </div>
                      </div>
                      
                      {/* FAQ */}
                      <div>
                        <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                          ❓ Questions fréquentes
                        </h4>
                        <div className="space-y-2">
                          {igTutorial.faq?.map((item, index) => (
                            <details key={index} className="bg-blue-50 rounded-lg border border-blue-200">
                              <summary className="cursor-pointer p-3 font-medium text-sm text-blue-800">
                                {item.question}
                              </summary>
                              <div className="px-3 pb-3 text-sm text-blue-700">
                                {item.answer}
                              </div>
                            </details>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
                    </div>
                  )}
                  
                  <div className="mt-6 pt-4 border-t">
                    <Button onClick={() => setIgTutorialOpen(false)} className="w-full bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600">
                      J'ai compris, commencer la configuration
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Brevo (Email) Channel - API Key Integration */}
            <Card className={`border-2 ${brevoStatus?.is_enabled ? 'border-green-300' : brevoStatus?.has_credentials ? 'border-yellow-300' : 'border-gray-200'}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 rounded bg-blue-600">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">Email – Brevo</h3>
                        <button 
                          onClick={() => setBrevoInfoOpen(true)}
                          className="p-1 rounded-full hover:bg-muted/80 transition-colors"
                          title="Plus d'informations"
                        >
                          <AlertCircle className="w-4 h-4 text-blue-500" />
                        </button>
                      </div>
                      {brevoStatus?.is_enabled ? (
                        <Badge className="bg-green-100 text-green-800 text-xs">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Activé
                        </Badge>
                      ) : brevoStatus?.has_credentials ? (
                        <Badge variant="outline" className="text-yellow-600 text-xs">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Configuré (désactivé)
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-500 text-xs">Non configuré</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground ml-9">Envoi automatique d'emails via Brevo (ex-Sendinblue)</p>
                  </div>
                  <Switch 
                    checked={brevoEnabled}
                    onCheckedChange={setBrevoEnabled}
                    disabled={!brevoStatus?.has_credentials}
                  />
                </div>

                {brevoLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Tutorial Accordion */}
                    <Accordion type="single" collapsible className="border rounded-lg">
                      <AccordionItem value="tutorial" className="border-none">
                        <AccordionTrigger className="px-4 py-3 hover:no-underline">
                          <div className="flex items-center gap-2 text-sm">
                            <HelpCircle className="w-4 h-4 text-blue-500" />
                            <span>Comment connecter Brevo ? (3 minutes)</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4 space-y-4 text-sm">
                          <div>
                            <h4 className="font-semibold text-blue-600 mb-2">Étape 1 — Créer un compte</h4>
                            <ol className="list-decimal ml-4 space-y-1 text-muted-foreground">
                              <li>Aller sur <a href="https://www.brevo.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">brevo.com</a></li>
                              <li>Créer un compte gratuit</li>
                              <li>Vérifier l'adresse email</li>
                            </ol>
                          </div>
                          <div>
                            <h4 className="font-semibold text-blue-600 mb-2">Étape 2 — Récupérer la clé API</h4>
                            <ol className="list-decimal ml-4 space-y-1 text-muted-foreground">
                              <li>Se connecter à Brevo</li>
                              <li>Aller dans : <strong>SMTP & API → API Keys</strong></li>
                              <li>Cliquer sur "Create a new API key"</li>
                              <li>Copier la clé générée (elle ne sera plus affichée)</li>
                            </ol>
                          </div>
                          <div>
                            <h4 className="font-semibold text-blue-600 mb-2">Étape 3 — Créer un email expéditeur</h4>
                            <ol className="list-decimal ml-4 space-y-1 text-muted-foreground">
                              <li>Aller dans : <strong>Senders & IP → Senders</strong></li>
                              <li>Ajouter une adresse expéditeur (ex: contact@mondomaine.com)</li>
                              <li>Valider l'adresse (email de confirmation)</li>
                            </ol>
                          </div>
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                            <h4 className="font-semibold text-yellow-800 mb-1">💡 Conseils</h4>
                            <ul className="text-xs text-yellow-700 space-y-1">
                              <li>• Utilisez un email de votre propre domaine pour une meilleure délivrabilité</li>
                              <li>• L'offre gratuite permet 300 emails/jour</li>
                              <li>• Aucune carte bancaire requise pour commencer</li>
                            </ul>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>

                    {/* Configuration Form */}
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Clé API Brevo {!brevoStatus?.has_credentials && <span className="text-red-500">*</span>}</Label>
                        <div className="relative">
                          <Input
                            type={showBrevoKey ? "text" : "password"}
                            value={brevoApiKey}
                            onChange={(e) => setBrevoApiKey(e.target.value)}
                            placeholder={brevoStatus?.has_credentials ? "••••••••••• (laisser vide pour conserver)" : "xkeysib-xxxxxxxxxxxx"}
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowBrevoKey(!showBrevoKey)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showBrevoKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {brevoStatus?.has_credentials && (
                          <p className="text-xs text-muted-foreground mt-1">Clé actuelle : {brevoStatus.api_key_masked}</p>
                        )}
                      </div>

                      <div>
                        <Label className="text-xs text-muted-foreground">Email expéditeur <span className="text-red-500">*</span></Label>
                        <Input
                          type="email"
                          value={brevoSenderEmail}
                          onChange={(e) => setBrevoSenderEmail(e.target.value)}
                          placeholder="contact@votredomaine.com"
                        />
                      </div>

                      <div>
                        <Label className="text-xs text-muted-foreground">Nom expéditeur <span className="text-red-500">*</span></Label>
                        <Input
                          value={brevoSenderName}
                          onChange={(e) => setBrevoSenderName(e.target.value)}
                          placeholder="Votre entreprise ou nom"
                        />
                      </div>

                      <div className="flex items-center gap-4 pt-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            id="brevo-auto"
                            checked={brevoAutoSend}
                            onCheckedChange={setBrevoAutoSend}
                          />
                          <Label htmlFor="brevo-auto" className="text-sm">Envoi automatique</Label>
                        </div>
                      </div>
                      {brevoAutoSend && (
                        <p className="text-xs text-muted-foreground bg-blue-50 p-2 rounded">
                          📧 Les articles validés seront envoyés automatiquement par email à la date planifiée.
                        </p>
                      )}

                      <div className="flex gap-2 pt-2">
                        <Button
                          onClick={handleSaveBrevoCredentials}
                          disabled={brevoSaving || (!brevoApiKey && !brevoStatus?.has_credentials) || !brevoSenderEmail || !brevoSenderName}
                          className="flex-1"
                        >
                          {brevoSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
                          Sauvegarder
                        </Button>
                        {brevoStatus?.has_credentials && (
                          <Button variant="destructive" size="icon" onClick={handleDeleteBrevoCredentials}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>

                      {brevoStatus?.has_credentials && (
                        <Button
                          variant="outline"
                          onClick={handleTestBrevo}
                          disabled={brevoTesting}
                          className="w-full"
                        >
                          {brevoTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                          Tester la connexion (envoyer un email)
                        </Button>
                      )}

                      {brevoStatus?.account_email && (
                        <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                          <p>Compte : {brevoStatus.account_email}</p>
                          <p>Plan : {brevoStatus.plan_type || 'Gratuit'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Brevo Info Modal */}
            {brevoInfoOpen && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setBrevoInfoOpen(false)}>
                <div className="bg-white rounded-lg p-6 max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2 mb-4">
                    <AlertCircle className="w-5 h-5 text-blue-500" />
                    <h3 className="font-semibold text-lg">Brevo – Offre gratuite</h3>
                  </div>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>• Jusqu'à <strong>300 emails / jour</strong></p>
                    <p>• Emails marketing et transactionnels inclus</p>
                    <p>• Pas d'OAuth requis</p>
                    <p>• Connexion par clé API</p>
                    <p>• Conforme RGPD (hébergement UE)</p>
                    <p>• Idéal pour newsletters et notifications automatiques</p>
                  </div>
                  <Button className="w-full mt-4" onClick={() => setBrevoInfoOpen(false)}>Fermer</Button>
                </div>
              </div>
            )}

            {/* Other Channels */}
            {CHANNELS.filter(ch => ch.id !== 'x' && ch.id !== 'telegram' && ch.id !== 'facebook').map((channel) => {
              const config = project.channels_config?.[channel.id] || { enabled: false };
              
              return (
                <Card key={channel.id} className="border opacity-60" data-testid={`channel-card-${channel.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium">{channel.name}</h3>
                          <Badge variant="outline" className="text-xs">Bientôt</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{channel.description}</p>
                      </div>
                      <Switch checked={false} disabled />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
